"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import {
  Activity,
  AppWindow,
  ArrowLeft,
  Boxes,
  Cable,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  Database,
  Gauge,
  HardDrive,
  LayoutDashboard,
  LoaderCircle,
  Network,
  Plus,
  RefreshCw,
  Search,
  ServerCog,
  ShieldCheck,
  Workflow,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { InfrastructureCompositionPanel } from "./infrastructure-composition-panel";
import { UniversalAccountsSetupPanel } from "./universal-accounts-setup-panel";
import { useRunyiBackTrail } from "@/lib/use-runyi-back-trail";
import { InfrastructureProjectsDirectory } from "./infrastructure-projects-directory";
import { ApplicationInfrastructureMenu, UniversalInfrastructureActions } from "./universal-infrastructure-actions";
import { UniversalAiModelPicker } from "./universal-ai-model-picker";
import { AppsConfigurationEngine, AppsOpenTestScreen } from "./apps-product-workspaces";
import { useRunyiValueSync } from "../lib/runyi-value-sync";
import { RunyiRegistryTabs, RunyiResponsiveActionGrid, RunyiResponsiveActionLabel, RUNYI_RESPONSIVE_ACTION_BUTTON_CLASS, useRunyiUiControlPlane } from "./runyi-ui-coordinator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export type EnterpriseWorkspaceMode =
  | "new"
  | "catalogue"
  | "applications"
  | "infrastructure"
  | "bridge"
  | "flow";

type CatalogueCategory = {
  category_key: string;
  display_name: string;
  description: string;
  sort_order: number;
};

type MarketplaceItem = {
  provider_capability_key: string;
  provider_key: string;
  display_name: string;
  service_type: string;
  logical_capability_hint?: string | null;
  state: string;
  category_key: string;
  runyi_coverage_state: string;
  installable: boolean;
  verified: boolean;
  installed_resource_count: number;
  assigned_application_count: number;
};

type EnterpriseResource = {
  resource_key: string;
  resource_type: string;
  display_name: string;
  provider_key?: string | null;
  tenant_project_ref?: string | null;
  region_ref?: string | null;
  capabilities?: string[];
  environment_key: string;
  authority_class: string;
  data_authority: string;
  lifecycle_state: string;
  health_state: string;
  immutable: boolean;
  identity_email: string;
  identity_email_type: string;
  identity_email_verified: boolean;
  identity_email_source: string;
  sync_state: string;
  sync_error?: string | null;
  last_synced_at?: string | null;
  provider_revision?: string | null;
  runyi_revision?: number;
  next_reconciliation_at?: string | null;
  metadata?: Record<string,unknown>;
};

type BackendServiceItem = {
  backend_service_key: string;
  display_name: string;
  logical_role: "API"|"BUSINESS_LOGIC"|"EVENT_PROCESSOR"|"BACKGROUND_JOB"|"SCHEDULED_JOB"|"WEBHOOK"|"REALTIME"|"NOTIFICATION"|"AI_ORCHESTRATION"|"INTEGRATION"|"CUSTOM";
  runtime_target_entity_key?: string | null;
  provider_key?: string | null;
  provider_project_ref?: string | null;
  environment_key: string;
  endpoint_ref?: string | null;
  version_ref?: string | null;
  source_repository?: string | null;
  git_commit_sha?: string | null;
  lifecycle_state: string;
  health_state: string;
  verification_state: string;
  identity_email: string;
  identity_email_type: string;
  identity_email_verified: boolean;
  identity_email_source: string;
  sync_state: string;
  sync_error?: string | null;
  last_synced_at?: string | null;
  last_verified_at?: string | null;
  sync_source?: string | null;
  next_reconciliation_at?: string | null;
  metadata?: Record<string,unknown>;
};

type SyncTargetItem = {
  target_key: string;
  target_kind: string;
  provider_key?: string | null;
  tenant_project_ref?: string | null;
  sync_state: "CURRENT"|"SYNCING"|"STALE"|"DRIFTED"|"ERROR"|"UNKNOWN";
  last_event_at?: string | null;
  last_synced_at?: string | null;
  last_verified_at?: string | null;
  sync_source?: string | null;
  sync_error?: string | null;
  next_reconciliation_at?: string | null;
};

type ProviderAccountHint = {
  provider_key: string;
  display_name?: string | null;
  lifecycle_state?: string | null;
  backend_stage?: string | null;
  target_account_ref?: string | null;
  target_project_ref?: string | null;
  last_verified_at?: string | null;
};

type ScopedSelectionContext = {
  source?: string;
  origin_route?: string;
  target_route?: string;
  entered_target?: boolean;
  application_entity_key?: string | null;
  application_display_name?: string | null;
  project_id?: string | null;
  project_display_name?: string | null;
  provider_capability_key?: string | null;
  provider_key?: string | null;
  category_key?: string | null;
  target_entity_key?: string | null;
  logical_capability?: string | null;
  environment_key?: string | null;
  target_scope?: "APPLICATION" | "PROJECT" | null;
  source_display_name?: string | null;
  provider_account_identity?: string | null;
  provider_project_ref?: string | null;
};

type InfrastructureDetailSelection = {
  kind: "RESOURCE" | "CAPABILITY" | "BACKEND_SERVICE";
  key: string;
};

type ApplicationItem = {
  entity_key: string;
  display_name: string;
  lifecycle_state: string;
  health_state: string;
  attributes?: Record<string, unknown>;
};

type Assignment = {
  assignment_id: string;
  application_entity_key: string;
  assignment_slot: string;
  assignment_role: string;
  target_entity_key: string;
  logical_capability?: string | null;
  provider_key?: string | null;
  environment_key: string;
  category_key?: string | null;
  state: string;
  source_of_truth: boolean;
  shared_resource: boolean;
  metadata?: Record<string, unknown>;
};

type DashboardProvider = {
  dashboard_provider_key: string;
  display_name: string;
  provider_key?: string | null;
  provider_class: string;
  hosting_model: string;
  integration_mode: string;
  coverage_state: string;
};

type DashboardInstance = {
  dashboard_id: string;
  dashboard_key: string;
  display_name: string;
  dashboard_provider_key: string;
  scope_class: "ENTERPRISE_CONTROL_TOWER" | "APPLICATION_SCOPED";
  application_entity_key?: string | null;
  dashboard_role: string;
  lifecycle_state: string;
  health_state: string;
  is_primary: boolean;
};

type ProvisioningRequest = {
  request_id: string;
  blueprint_key: string;
  status: string;
  provider_key?: string | null;
  environment_key: string;
  tenant_project_ref?: string | null;
  approval_state: string;
  cost_state: string;
  cost_confirmation_ref?: string | null;
  reason?: string | null;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

type ProvisioningStep = {
  step_id: string;
  request_id: string;
  step_order: number;
  step_key: string;
  step_type: string;
  action_key?: string | null;
  provider_operation_id?: string | null;
  status: string;
  attempt_count: number;
  result_summary?: Record<string, unknown>;
  verification_evidence?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  updated_at: string;
};

type ApplicationManifest = {
  manifest_id: string;
  manifest_key: string;
  target_entity_key: string;
  revision: number;
  version: string;
  schema_version: string;
  status: string;
  content_hash: string;
  published_at?: string | null;
  metadata?: Record<string, unknown>;
};

type ApplicationRelease = {
  release_id: string;
  release_key: string;
  application_entity_key: string;
  environment_key: "DEV" | "STAGING" | "CANARY" | "PRODUCTION";
  tenant_project_ref?: string | null;
  manifest_id: string;
  desired_state_id: string;
  plan_id: string;
  release_number: number;
  version: string;
  state: string;
  health_state: string;
  is_current: boolean;
  previous_release_id?: string | null;
  source_repository?: string | null;
  git_commit_sha?: string | null;
  artifact_sha256?: string | null;
  activated_at?: string | null;
  retired_at?: string | null;
  created_at: string;
  updated_at: string;
  metadata?: Record<string, unknown>;
};

type EnvironmentPromotion = {
  promotion_id: string;
  application_entity_key: string;
  source_release_id: string;
  target_release_id?: string | null;
  source_environment_key: string;
  target_environment_key: string;
  status: string;
  reason: string;
  created_at: string;
  completed_at?: string | null;
};

type EnvironmentItem = {
  environment_key: "DEV" | "STAGING" | "CANARY" | "PRODUCTION";
  display_name: string;
  promotion_order: number;
  protected: boolean;
  active: boolean;
};

type DeploymentPlanStep = {
  step_id: string;
  plan_id: string;
  step_order: number;
  step_key: string;
  action_class: string;
  target_entity_key?: string | null;
  requirement_key?: string | null;
  logical_capability?: string | null;
  resource_type?: string | null;
  blueprint_key?: string | null;
  provider_key?: string | null;
  action_key?: string | null;
  provisioning_request_id?: string | null;
  provider_operation_id?: string | null;
  risk_level: string;
  status: string;
  depends_on_step_keys?: string[];
  input_spec?: Record<string, unknown>;
  compiled_spec?: Record<string, unknown>;
  result_summary?: Record<string, unknown>;
  verification_evidence?: Record<string, unknown>;
};

type ProviderOperation = {
  operation_id: string;
  action_key: string;
  provider_key: string;
  adapter_key: string;
  environment_key: string;
  tenant_project_ref?: string | null;
  status: string;
  approval_required: boolean;
  approval_id?: string | null;
  verification_profile?: string | null;
  verification_evidence?: Record<string, unknown>;
};

type SupabaseOauthConnection = {
  id: string;
  tenant_project_ref: string;
  channel: string;
  status: string;
  provider: string;
  granted_scopes?: string[];
  token_expires_at?: string | null;
  last_verified_at?: string | null;
};

type ApplicationBlueprint = {
  blueprint_id: string;
  blueprint_key: string;
  revision: number;
  version: string;
  display_name: string;
  application_class: string;
  category: string;
  description: string;
  status: string;
  parameter_schema?: {
    type?: string;
    properties?: Record<string, {
      type?: string;
      default?: unknown;
      enum?: unknown[];
      minLength?: number;
      maxLength?: number;
      minimum?: number;
      maximum?: number;
    }>;
    required?: string[];
    additionalProperties?: boolean;
  };
  stack_template?: Record<string, unknown>;
  default_environment: "DEV" | "STAGING" | "CANARY" | "PRODUCTION";
  content_hash: string;
  published_at?: string | null;
  metadata?: Record<string, unknown>;
};

type ApplicationFactoryRun = {
  factory_run_id: string;
  run_key: string;
  idempotency_hash: string;
  blueprint_id: string;
  application_key: string;
  display_name: string;
  application_entity_key?: string | null;
  environment_key: string;
  tenant_project_ref?: string | null;
  status: string;
  parameters?: Record<string, unknown>;
  provider_overrides?: Record<string, unknown>;
  resource_overrides?: Record<string, unknown>;
  provider_resolution?: Array<Record<string, unknown>>;
  manifest_id?: string | null;
  desired_state_id?: string | null;
  plan_id?: string | null;
  release_id?: string | null;
  error_code?: string | null;
  error_message?: string | null;
  created_at: string;
  completed_at?: string | null;
  updated_at: string;
  metadata?: Record<string, unknown>;
};

type FactoryPreview = {
  blueprint_id: string;
  blueprint_key: string;
  blueprint_revision: number;
  blueprint_version: string;
  application_key: string;
  application_entity_key: string;
  display_name: string;
  environment_key: string;
  tenant_project_ref?: string | null;
  effective_parameters?: Record<string, unknown>;
  rendered_stack_manifest?: Record<string, unknown>;
  provider_resolution?: Array<{
    slot?: string;
    required?: boolean;
    status?: string;
    logical_capability?: string;
    resource_type?: string;
    provider_key?: string;
    resource_key?: string;
    profile_key?: string;
    blueprint_key?: string;
    action_key?: string;
    coverage_state?: string;
    oauth_ready?: boolean;
    reason?: string;
  }>;
  readiness: string;
  required_blocked_count: number;
  optional_blocked_count: number;
  oauth_not_ready_count: number;
  idempotency_hash: string;
  mutating: false;
};

type Overview = {
  architecture_version: string;
  value_snapshot?: {
    snapshot_revision: string;
    architecture_version: string;
    source_authority: string;
    source_revision: string;
    canonical_commit: string;
    deployment_id: string;
    observed_at: string;
    verified_at: string;
    freshness_state: "CURRENT" | "SYNCING" | "STALE" | "UNKNOWN" | "ERROR" | "DRIFT";
    verification_state: string;
    tenant_scope: string;
  };
  value_authorities?: Record<string, string>;
  control_tower_scope: string;
  categories: CatalogueCategory[];
  marketplace: MarketplaceItem[];
  resources: EnterpriseResource[];
  applications: ApplicationItem[];
  assignments: Assignment[];
  backend_services?: BackendServiceItem[];
  sync_targets?: SyncTargetItem[];
  sync_runs?: Array<{ sync_run_id:string; trigger_source:string; status:string; started_at:string; completed_at?:string|null; error_count:number }>;
  dashboard_providers: DashboardProvider[];
  dashboards: DashboardInstance[];
  provisioning_requests?: ProvisioningRequest[];
  provisioning_steps?: ProvisioningStep[];
  deployment_plan_steps?: DeploymentPlanStep[];
  application_manifests?: ApplicationManifest[];
  provider_operations?: ProviderOperation[];
  application_releases?: ApplicationRelease[];
  environment_promotions?: EnvironmentPromotion[];
  environments?: EnvironmentItem[];
  supabase_oauth_connections?: SupabaseOauthConnection[];
  application_blueprints?: ApplicationBlueprint[];
  application_factory_runs?: ApplicationFactoryRun[];
  flow?: {
    provisioning?: { total_recent?: number; pending?: number; completed?: number };
    deployment_plans?: { total_recent?: number; ready?: number; blocked?: number; completed?: number };
  };
  summary?: Record<string, number>;
  access?: {
    role?: string;
    resource_read?: boolean;
    resource_manage?: boolean;
    universal_manage?: boolean;
  };
};

type MarketplaceFastSummary = {
  architecture_version?: string | null;
  architecture_commit?: string | null;
  state_revision?: number | string;
  summary?: {
    applications?: number;
    provider_capabilities?: number;
    installed_resources?: number;
    assignments?: number;
    active_assignments?: number;
    backend_services?: number;
    sync_targets?: number;
    sync_current?: number;
    sync_stale?: number;
    sync_drift?: number;
    sync_error?: number;
    latest_sync_status?: string | null;
  };
};

const MARKETPLACE_FAST_CACHE_KEY = "runyi:marketplace-fast-summary:v2";
const RUNYI_STARTUP_OVERVIEW_CACHE_KEY = "runyi:ui:startup-overview:v1";

function readCachedValue<T>(key: string): T | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : null;
  } catch {
    return null;
  }
}

function writeCachedValue(key: string, value: unknown) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Cache writes are best effort only; live RUNYI truth remains authoritative.
  }
}

type DisplayIdentityResolution = {
  email: string;
  source: "OBJECT" | "CAPABILITY_INSTANCE" | "PROVIDER_ACCOUNT" | "OWNER_ACCOUNT" | "NOT_CONNECTED";
};

function uniqueEmails(values: Array<string | null | undefined>) {
  return Array.from(new Set(values.map((value) => String(value ?? "").trim().toLowerCase()).filter((value) => value.includes("@"))));
}

function ownerDisplayEmail(overview: Overview | null) {
  const root = (overview?.resources ?? []).find((item) => item.resource_key === "ROOT:OWNER_SUPABASE" || item.authority_class === "ENTERPRISE_ROOT");
  return root?.identity_email?.trim() || null;
}

function verifiedProviderEmails(overview: Overview | null, providerKey?: string | null) {
  const provider = String(providerKey ?? "").toUpperCase();
  if (!provider) return [] as string[];
  return uniqueEmails((overview?.resources ?? [])
    .filter((item) => String(item.provider_key ?? "").toUpperCase() === provider && item.identity_email_verified !== false)
    .map((item) => item.identity_email));
}

function resolveCapabilityDisplayIdentity(overview: Overview | null, item: MarketplaceItem): DisplayIdentityResolution {
  const capabilityEmails = uniqueEmails((overview?.resources ?? [])
    .filter((resource) => String(resource.metadata?.provider_capability_key ?? "") === item.provider_capability_key && resource.identity_email_verified !== false)
    .map((resource) => resource.identity_email));
  if (capabilityEmails.length === 1) return { email: capabilityEmails[0], source: "CAPABILITY_INSTANCE" };
  const providerEmails = verifiedProviderEmails(overview, item.provider_key);
  if (providerEmails.length === 1) return { email: providerEmails[0], source: "PROVIDER_ACCOUNT" };
  const owner = ownerDisplayEmail(overview);
  if (owner) return { email: owner, source: "OWNER_ACCOUNT" };
  return { email: "NOT CONNECTED", source: "NOT_CONNECTED" };
}

function resolveBackendServiceDisplayIdentity(overview: Overview | null, item: BackendServiceItem): DisplayIdentityResolution {
  if (item.identity_email?.trim()) return { email: item.identity_email.trim(), source: "OBJECT" };
  const providerEmails = verifiedProviderEmails(overview, item.provider_key);
  if (providerEmails.length === 1) return { email: providerEmails[0], source: "PROVIDER_ACCOUNT" };
  const owner = ownerDisplayEmail(overview);
  if (owner) return { email: owner, source: "OWNER_ACCOUNT" };
  return { email: "NOT CONNECTED", source: "NOT_CONNECTED" };
}

function resolveResourceDisplayIdentity(overview: Overview | null, item: EnterpriseResource): DisplayIdentityResolution {
  if (item.identity_email?.trim()) return { email: item.identity_email.trim(), source: "OBJECT" };
  // Never borrow another member's provider identity for a tenant/project-scoped resource.
  if (item.tenant_project_ref) return { email: "NOT CONNECTED", source: "NOT_CONNECTED" };
  const providerEmails = verifiedProviderEmails(overview, item.provider_key);
  if (providerEmails.length === 1) return { email: providerEmails[0], source: "PROVIDER_ACCOUNT" };
  const owner = ownerDisplayEmail(overview);
  if (owner) return { email: owner, source: "OWNER_ACCOUNT" };
  return { email: "NOT CONNECTED", source: "NOT_CONNECTED" };
}

function displayIdentitySource(source: DisplayIdentityResolution["source"]) {
  if (source === "OWNER_ACCOUNT") return "OWNER";
  if (source === "PROVIDER_ACCOUNT") return "PROVIDER";
  if (source === "CAPABILITY_INSTANCE") return "CONNECTED INSTANCE";
  if (source === "OBJECT") return "VERIFIED OBJECT";
  return "NOT CONNECTED";
}

const ASSIGNMENT_SLOTS = [
  "PRIMARY_DATABASE",
  "VECTOR_DATABASE",
  "PRIMARY_STORAGE",
  "BACKUP_STORAGE",
  "FRONTEND_HOSTING",
  "API_RUNTIME",
  "BACKEND_SERVICE",
  "AUTHENTICATION",
  "QUEUE",
  "CACHE",
  "SEARCH",
  "NOTIFICATIONS",
  "ANALYTICS",
  "MONITORING",
  "AI_PROVIDER",
  "DASHBOARD",
  "BACKUP_RECOVERY",
] as const;

function tone(value?: string | null) {
  const state = String(value ?? "").toUpperCase();
  if (["ACTIVE","HEALTHY","VERIFIED","PASS","EXECUTABLE","INSTALLED","COMPOSED","CURRENT"].includes(state)) {
    return "bg-emerald-400/10 text-emerald-500 dark:text-emerald-300";
  }
  if (["PREPARABLE","DESIRED","READY","WARN","DEGRADED","PARTIAL","AUTHORIZATION_REQUIRED","CREATING","PREVIEWED","SYNCING","STALE"].includes(state)) {
    return "bg-amber-400/10 text-amber-600 dark:text-amber-200";
  }
  if (["FAILED","FAIL","CRITICAL","BLOCKED","RETIRED","DRIFTED","ERROR"].includes(state)) {
    return "bg-red-400/10 text-red-500 dark:text-red-300";
  }
  return "bg-foreground/[0.06] text-foreground/60";
}


function categoryForResourceType(resourceType: string) {
  const value = String(resourceType || "").toUpperCase();
  const direct: Record<string,string> = {
    DATABASE: "DATABASE", D1_DATABASE: "DATABASE", FIRESTORE_DATABASE: "DATABASE",
    VECTOR_DATABASE: "VECTOR_DATABASE", OBJECT_STORAGE: "STORAGE", STORAGE: "STORAGE", R2_BUCKET: "STORAGE",
    KV_NAMESPACE: "CACHE", CACHE: "CACHE", QUEUE: "QUEUE_MESSAGING", MESSAGING: "QUEUE_MESSAGING",
    WORKER: "RUNTIME", SERVERLESS: "RUNTIME", EDGE_COMPUTE: "RUNTIME", DEPLOYMENT: "HOSTING", HOSTING: "HOSTING",
    DNS_RECORD: "NETWORK_EDGE", DNS: "NETWORK_EDGE", EDGE_SECURITY: "NETWORK_EDGE", OBSERVABILITY: "ANALYTICS_OBSERVABILITY",
    ANALYTICS: "ANALYTICS_OBSERVABILITY", IDENTITY: "AUTH_IDENTITY", AUTH: "AUTH_IDENTITY", SOURCE_CONTROL: "SOURCE_CONTROL",
    RECOVERY: "RECOVERY_BACKUP", AUTOMATION_RUNTIME: "WORKFLOW_AUTOMATION",
  };
  return direct[value] ?? (value.includes("DATABASE") ? "DATABASE" : value.includes("STORAGE") || value.includes("BUCKET") ? "STORAGE" : "FUTURE_RESOURCE");
}

function titleForMode(mode: EnterpriseWorkspaceMode) {
  if (mode === "new") return ["New", "Create complete applications from Factory blueprints or advanced stack definitions."];
  if (mode === "catalogue") return ["Catalogue", "Everything RUNYI knows across databases, storage, hosting, runtimes, services and future providers."];
  if (mode === "applications") return ["Applications & Projects", "Operate applications, Infrastructure Projects and their complete infrastructure compositions."];
  if (mode === "infrastructure") return ["Marketplace", "State what you need. RUNYI resolves the capability and keeps execution, verification and reconciliation on one governed automation path."];
  if (mode === "bridge") return ["Bridge", "Recommend, compose, assign and change application infrastructure without bypassing Owner choice, provisioning or approval."];
  return ["Flow", "Provisioning, deployment, reconciliation and infrastructure orchestration."];
}

export function EnterpriseInfrastructureWorkspace({
  supabase,
  connected,
  mode,
}: {
  supabase: SupabaseClient | null;
  connected: boolean;
  mode: EnterpriseWorkspaceMode;
}) {
  const [overview, setOverview] = useState<Overview | null>(() => readCachedValue<Overview>(RUNYI_STARTUP_OVERVIEW_CACHE_KEY));
  const [fastSummary, setFastSummary] = useState<MarketplaceFastSummary | null>(() => readCachedValue<MarketplaceFastSummary>(MARKETPLACE_FAST_CACHE_KEY));
  const [providerAccountHints,setProviderAccountHints] = useState<ProviderAccountHint[]>([]);
  const [selectionContext,setSelectionContext] = useState<ScopedSelectionContext | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("ALL");
  const [provider, setProvider] = useState("ALL");
  const [marketplaceIntent, setMarketplaceIntent] = useState("");
  const marketplaceUiControlPlane = useRunyiUiControlPlane({ supabase, connected, screenKey: "MARKETPLACE" });
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);
  const [applicationsSection, setApplicationsSection] = useState<"apps" | "projects" | "compositions">("apps");
  const [expandedApplication, setExpandedApplication] = useState<string | null>(null);
  const [appsConfigKey, setAppsConfigKey] = useState<string | null>(null);
  const [appsTestTarget, setAppsTestTarget] = useState<{ appKey: string | null; projectName: string | null } | null>(null);
  const [projectCreateToken, setProjectCreateToken] = useState(0);
  const [infrastructureSection, setInfrastructureSection] = useState<"overview" | "marketplace" | "projects" | "resources" | "accounts" | "telemetry">("marketplace");
  const [infrastructureDetail, setInfrastructureDetail] = useState<InfrastructureDetailSelection | null>(null);
  const [flowSection, setFlowSection] = useState<"overview" | "releases" | "authorization" | "provisioning">("overview");

  // UI history is per active workspace and only tracks function navigation,
  // never provider sync data, credentials or form drafts.
  useRunyiBackTrail({
    applicationsSection,
    expandedApplication,
    appsConfigKey,
    appsTestTarget,
    infrastructureSection,
    infrastructureDetail,
    flowSection,
  }, (previous) => {
    setApplicationsSection(previous.applicationsSection);
    setExpandedApplication(previous.expandedApplication);
    setAppsConfigKey(previous.appsConfigKey);
    setAppsTestTarget(previous.appsTestTarget);
    setInfrastructureSection(previous.infrastructureSection);
    setInfrastructureDetail(previous.infrastructureDetail);
    setFlowSection(previous.flowSection);
  }, mode !== "new");


  const [newAppName, setNewAppName] = useState("");
  const [newAppKey, setNewAppKey] = useState("");
  const [backendServiceName,setBackendServiceName] = useState("");
  const [backendServiceKey,setBackendServiceKey] = useState("");
  const [backendServiceRole,setBackendServiceRole] = useState<BackendServiceItem["logical_role"]>("API");
  const [backendServiceRuntime,setBackendServiceRuntime] = useState("");

  const [factoryBlueprintKey, setFactoryBlueprintKey] = useState("WEB_APP");
  const [factoryAppName, setFactoryAppName] = useState("");
  const [factoryAppKey, setFactoryAppKey] = useState("");
  const [factoryEnvironment, setFactoryEnvironment] = useState<"DEV" | "STAGING" | "CANARY" | "PRODUCTION">("DEV");
  const [factoryTenantProjectRef, setFactoryTenantProjectRef] = useState("");
  const [factoryParameters, setFactoryParameters] = useState("{}");
  const [factoryProviderOverrides, setFactoryProviderOverrides] = useState("{}");
  const [factoryResourceOverrides, setFactoryResourceOverrides] = useState("{}");
  const [factoryPreview, setFactoryPreview] = useState<FactoryPreview | null>(null);
  const [factoryApplicationApprovalId, setFactoryApplicationApprovalId] = useState("");
  const [factoryManifestApprovalId, setFactoryManifestApprovalId] = useState("");
  const [factoryDesiredApprovalId, setFactoryDesiredApprovalId] = useState("");
  const [factoryCompositionApprovalId, setFactoryCompositionApprovalId] = useState("");

  const [customBlueprintKey, setCustomBlueprintKey] = useState("");
  const [customBlueprintName, setCustomBlueprintName] = useState("");
  const [customBlueprintVersion, setCustomBlueprintVersion] = useState("1.0.0");
  const [customBlueprintClass, setCustomBlueprintClass] = useState("GENERAL");
  const [customBlueprintCategory, setCustomBlueprintCategory] = useState("CUSTOM");
  const [customBlueprintSchema, setCustomBlueprintSchema] = useState(JSON.stringify({
    type: "object",
    properties: {},
    required: [],
    additionalProperties: false,
  }, null, 2));
  const [customBlueprintStack, setCustomBlueprintStack] = useState(JSON.stringify({
    stack: {
      resources: [],
      services: [],
      relationships: [],
      dashboard: {
        provider_key: "RUNYI_NATIVE",
        dashboard_role: "OPERATIONS",
        display_name: "{{DISPLAY_NAME}} Dashboard",
      },
    },
  }, null, 2));
  const [customBlueprintApprovalId, setCustomBlueprintApprovalId] = useState("");

  const [stackApp, setStackApp] = useState("");
  const [stackManifestKey, setStackManifestKey] = useState("");
  const [stackVersion, setStackVersion] = useState("1.0.0");
  const [stackEnvironment, setStackEnvironment] = useState<"DEV" | "STAGING" | "CANARY" | "PRODUCTION">("DEV");
  const [stackTenantProjectRef, setStackTenantProjectRef] = useState("");
  const [stackManifestJson, setStackManifestJson] = useState(JSON.stringify({
    stack: {
      resources: [
        {
          slot: "PRIMARY_DATABASE",
          logical_capability: "relational.query",
          resource_type: "DATABASE",
          reuse_policy: "PREFER_EXISTING",
          sharing_policy: "OWNER_CHOICE",
          source_of_truth: true,
        },
      ],
      services: [],
      dashboard: {
        provider_key: "RUNYI_NATIVE",
        dashboard_role: "OPERATIONS",
      },
      relationships: [],
    },
  }, null, 2));
  const [publishedManifestId, setPublishedManifestId] = useState("");

  const [selectedApp, setSelectedApp] = useState("");
  const [selectedSlot, setSelectedSlot] = useState("PRIMARY_DATABASE");
  const [selectedTarget, setSelectedTarget] = useState("");
  const [tenantProjectRef, setTenantProjectRef] = useState("");

  const [flowAssignmentId, setFlowAssignmentId] = useState("");
  const [flowProviderPayload, setFlowProviderPayload] = useState("{}");
  const [flowResourceSpec, setFlowResourceSpec] = useState("{}");
  const [flowCostRef, setFlowCostRef] = useState("");

  const [dashboardApp, setDashboardApp] = useState("");
  const [dashboardName, setDashboardName] = useState("");
  const [dashboardKey, setDashboardKey] = useState("");
  const [dashboardProvider, setDashboardProvider] = useState("RUNYI_NATIVE");
  const [scopeProjectRef, setScopeProjectRef] = useState("");
  const [scopeReadiness, setScopeReadiness] = useState<Record<string, unknown> | null>(null);

  const refreshFastSummary = useCallback(async (showLoading = false) => {
    if (!supabase || !connected) return false;
    if (showLoading) setLoading(true);
    const screen = mode === "new" ? "NEW_INFRASTRUCTURE" : mode === "applications" ? "APPS" : "MARKETPLACE";
    const applySnapshot = (envelope: any) => {
      const nextOverview = (envelope?.payload?.startup_overview ?? null) as Overview | null;
      if (!nextOverview) return false;
      setOverview(nextOverview);
      writeCachedValue(RUNYI_STARTUP_OVERVIEW_CACHE_KEY, nextOverview);
      const nextSummary: MarketplaceFastSummary = {
        architecture_version: nextOverview.architecture_version,
        state_revision: envelope?.source_revision ?? envelope?.current_revision ?? null,
        summary: nextOverview.summary ?? {},
      };
      setFastSummary(nextSummary);
      writeCachedValue(MARKETPLACE_FAST_CACHE_KEY, nextSummary);
      setNotice(null);
      setLastUpdatedAt(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
      return true;
    };
    try {
      const { data, error } = await supabase.functions.invoke("runyi-ui-gateway", { body: { screen, mode: "read" } });
      if (error) throw error;
      if (data?.error) throw new Error(String(data.error));
      const applied = applySnapshot(data);
      if (!applied) throw new Error("RUNYI UI startup snapshot is unavailable.");
      if (data?.stale === true) {
        void supabase.functions.invoke("runyi-ui-gateway", { body: { screen, mode: "refresh" } }).then(({ data: fresh, error: refreshError }) => {
          if (!refreshError && !fresh?.error) applySnapshot(fresh);
        });
      }
      return true;
    } catch (error) {
      const cached = readCachedValue<Overview>(RUNYI_STARTUP_OVERVIEW_CACHE_KEY);
      if (cached) setOverview((current) => current ?? cached);
      else setNotice(error instanceof Error ? error.message : "Could not load RUNYI UI state.");
      return Boolean(cached);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [connected, mode, supabase]);

  const refreshDetails = useCallback(async (showLoading = true) => {
    if (!supabase || !connected) return false;
    if (showLoading) setLoading(true);
    setNotice(null);
    try {
      const [overviewResult,accountHintsResult] = await Promise.all([
        supabase.functions.invoke("runyi-control-tower", {
          body: {
            operation: "enterprise_infrastructure_overview",
            limit: 200,
          },
        }),
        supabase
          .from("runyi_new_infrastructure_setups")
          .select("provider_key,display_name,lifecycle_state,backend_stage,target_account_ref,target_project_ref,last_verified_at")
          .in("lifecycle_state",["READY","ACTIVE"])
          .order("updated_at",{ascending:false})
          .limit(500),
      ]);
      if (overviewResult.error) throw overviewResult.error;
      if (overviewResult.data?.error) throw new Error(String(overviewResult.data.error));
      setOverview((overviewResult.data ?? null) as Overview | null);
      setProviderAccountHints((accountHintsResult.data ?? []) as ProviderAccountHint[]);
      setLastUpdatedAt(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
      return true;
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Could not load RUNYI Enterprise infrastructure details.");
      return false;
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [connected, supabase]);

  const refresh = useCallback(async (showLoading = true) => {
    const startupOk = await refreshFastSummary(showLoading);
    if (startupOk && (mode === "bridge" || mode === "flow")) void refreshDetails(false);
    return startupOk;
  }, [mode, refreshDetails, refreshFastSummary]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.sessionStorage.getItem("runyi:selection-context");
      const next = raw ? JSON.parse(raw) as ScopedSelectionContext : null;
      setSelectionContext(next);
      if (mode === "applications" && next?.target_scope === "PROJECT") setApplicationsSection("projects");
      if (mode === "applications" && next?.target_scope === "APPLICATION") setApplicationsSection("apps");
    } catch {
      setSelectionContext(null);
    }
  },[mode]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const key = `runyi:workspace-state:${mode}`;
    try {
      const raw = window.sessionStorage.getItem(key);
      if (!raw) return;
      const state = JSON.parse(raw) as {
        search?: string;
        category?: string;
        provider?: string;
        applicationsSection?: "apps"|"projects"|"compositions";
        infrastructureSection?: "overview"|"marketplace"|"projects"|"resources"|"accounts"|"telemetry";
        expandedApplication?: string | null;
      };
      if (typeof state.search === "string") setSearch(state.search);
      if (typeof state.category === "string") setCategory(state.category);
      if (typeof state.provider === "string") setProvider(state.provider);
      if (state.applicationsSection) setApplicationsSection(state.applicationsSection);
      if (state.infrastructureSection) setInfrastructureSection(state.infrastructureSection);
      if (state.expandedApplication !== undefined) setExpandedApplication(state.expandedApplication);
    } catch {
      // Invalid local workspace state is ignored.
    }
  },[mode]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.sessionStorage.setItem(`runyi:workspace-state:${mode}`,JSON.stringify({
        search,category,provider,applicationsSection,infrastructureSection,expandedApplication,
      }));
    } catch {
      // Workspace state persistence is best effort only.
    }
  },[applicationsSection,category,expandedApplication,infrastructureSection,mode,provider,search]);

  useEffect(() => {
    if (!connected || !supabase) return;
    void refreshFastSummary(false);
  }, [connected, mode, refreshFastSummary, supabase]);

  const valueSync = useRunyiValueSync({
    supabase, connected,
    domains: ["catalogue", "infrastructure", "applications", "projects", "identity", "ai"],
    onRefresh: () => refreshFastSummary(false),
    intervalMs: 60_000,
  });

  const providerAccountHintMap = useMemo(() => {
    const map = new Map<string,ProviderAccountHint>();
    for (const hint of providerAccountHints) {
      const providerKey = String(hint.provider_key ?? "").toUpperCase();
      for (const ref of [hint.target_project_ref,hint.target_account_ref]) {
        if (!ref) continue;
        const key = `${providerKey}:${String(ref)}`;
        if (!map.has(key)) map.set(key,hint);
      }
    }
    return map;
  },[providerAccountHints]);

  const providerAccountEmail = useCallback((hint: ProviderAccountHint | null | undefined) => {
    const value=String(hint?.target_account_ref ?? "").trim();
    return value.includes("@") ? value : null;
  },[]);

  const providerAccountForResource = useCallback((resource: EnterpriseResource) => {
    const providerKey = String(resource.provider_key ?? "").toUpperCase();
    const refs = [
      resource.tenant_project_ref,
      typeof resource.metadata?.provider_project_ref === "string" ? resource.metadata.provider_project_ref : null,
      typeof resource.metadata?.provider_account_ref === "string" ? resource.metadata.provider_account_ref : null,
    ].filter((value): value is string => Boolean(value));
    for (const ref of refs) {
      const hint = providerAccountHintMap.get(`${providerKey}:${ref}`);
      if (hint) return hint;
    }
    return null;
  },[providerAccountHintMap]);

  const scopedTargetEntityKey = useMemo(() => {
    if (!selectionContext) return null;
    if (selectionContext.provider_capability_key) return `PROVIDER_CAPABILITY:${selectionContext.provider_capability_key}`;
    if (!selectionContext.target_entity_key) return null;
    const raw = selectionContext.target_entity_key;
    if (raw.includes(":")) return raw;
    return `RESOURCE:${raw}`;
  },[selectionContext]);

  const marketplace = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (overview?.marketplace ?? []).filter((item) => {
      if (category !== "ALL" && item.category_key !== category) return false;
      if (provider !== "ALL" && item.provider_key !== provider) return false;
      if (!needle) return true;
      return [
        item.display_name,
        item.provider_key,
        item.service_type,
        item.logical_capability_hint,
        item.category_key,
        resolveCapabilityDisplayIdentity(overview, item).email,
        JSON.stringify(item),
      ].filter(Boolean).join(" ").toLowerCase().includes(needle);
    });
  }, [category, overview, provider, search]);

  const resources = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (overview?.resources ?? []).filter((item) => {
      if (provider !== "ALL" && item.provider_key !== provider) return false;
      if (!needle) return true;
      return [
        item.display_name,item.resource_key,item.resource_type,item.provider_key,item.environment_key,
        providerAccountForResource(item)?.target_account_ref,
        providerAccountForResource(item)?.target_project_ref,
        JSON.stringify(item),
      ].filter(Boolean).join(" ").toLowerCase().includes(needle);
    });
  }, [overview, provider, providerAccountForResource, search]);

  const connectedProviderProjects = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (overview?.resources ?? [])
      .filter((item) => {
        if (String(item.provider_key ?? "").toUpperCase() !== "SUPABASE") return false;
        const isProject = item.resource_type === "PROJECT"
          || item.authority_class === "ENTERPRISE_ROOT"
          || item.resource_key === "ROOT:OWNER_SUPABASE";
        if (!isProject) return false;
        if (provider !== "ALL" && item.provider_key !== provider) return false;
        if (!needle) return true;
        return [
          item.display_name,
          item.resource_key,
          item.tenant_project_ref,
          item.identity_email,
          item.lifecycle_state,
          item.health_state,
          item.sync_state,
        ].filter(Boolean).join(" ").toLowerCase().includes(needle);
      })
      .sort((a,b) => {
        const rootOrder = Number(b.authority_class === "ENTERPRISE_ROOT") - Number(a.authority_class === "ENTERPRISE_ROOT");
        return rootOrder || a.display_name.localeCompare(b.display_name);
      });
  }, [overview, provider, search]);

  const backendServices = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (overview?.backend_services ?? []).filter((item) => {
      if (category !== "ALL" && category !== "BACKEND_SERVICE") return false;
      if (provider !== "ALL" && item.provider_key !== provider) return false;
      if (!needle) return true;
      return [
        item.display_name,item.backend_service_key,item.logical_role,item.provider_key,
        resolveBackendServiceDisplayIdentity(overview, item).email,item.runtime_target_entity_key,item.environment_key,item.sync_state,
      ].filter(Boolean).join(" ").toLowerCase().includes(needle);
    });
  },[category,overview,provider,search]);

  const filteredApplications = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return overview?.applications ?? [];
    return (overview?.applications ?? []).filter((app) => {
      const related = {
        app,
        assignments: (overview?.assignments ?? []).filter((item) => item.application_entity_key === app.entity_key),
        dashboards: (overview?.dashboards ?? []).filter((item) => item.application_entity_key === app.entity_key),
        releases: (overview?.application_releases ?? []).filter((item) => item.application_entity_key === app.entity_key),
      };
      return JSON.stringify(related).toLowerCase().includes(needle);
    });
  }, [overview, search]);

  // An Application is a project with a verified active Production release.
  // Registered entities without that release remain in the Projects stage.
  const isFinishedApplication = useCallback((key: string) =>
    (overview?.application_releases ?? []).some((release) =>
      release.application_entity_key === key && release.environment_key === "PRODUCTION"
      && release.is_current && release.state === "ACTIVE" && release.health_state === "HEALTHY"),
    [overview?.application_releases]);
  const finishedApplications = filteredApplications.filter((app) => isFinishedApplication(app.entity_key));
  const draftApplications = filteredApplications.filter((app) => !isFinishedApplication(app.entity_key));
  const configApplication = (overview?.applications ?? []).find((app) => app.entity_key === appsConfigKey) ?? null;
  const testedApplication = (overview?.applications ?? []).find((app) => app.entity_key === appsTestTarget?.appKey) ?? null;

  const filteredFlowReleases = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return overview?.application_releases ?? [];
    return (overview?.application_releases ?? []).filter((release) => {
      const related = {
        release,
        application: (overview?.applications ?? []).find((item) => item.entity_key === release.application_entity_key),
        steps: (overview?.deployment_plan_steps ?? []).filter((item) => item.plan_id === release.plan_id),
      };
      return JSON.stringify(related).toLowerCase().includes(needle);
    });
  }, [overview, search]);

  const filteredProvisioningRequests = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return overview?.provisioning_requests ?? [];
    return (overview?.provisioning_requests ?? []).filter((request) => {
      const related = {
        request,
        steps: (overview?.provisioning_steps ?? []).filter((item) => item.request_id === request.request_id),
      };
      return JSON.stringify(related).toLowerCase().includes(needle);
    });
  }, [overview, search]);

  const providers = useMemo(
    () => Array.from(new Set((overview?.marketplace ?? []).map((item) => item.provider_key))).sort(),
    [overview],
  );

  const factoryBlueprints = useMemo(() => {
    const latest = new Map<string, ApplicationBlueprint>();
    for (const blueprint of overview?.application_blueprints ?? []) {
      const current = latest.get(blueprint.blueprint_key);
      if (!current || blueprint.revision > current.revision) latest.set(blueprint.blueprint_key, blueprint);
    }
    return Array.from(latest.values()).sort((a,b) => a.display_name.localeCompare(b.display_name));
  }, [overview]);

  const selectedFactoryBlueprint = useMemo(
    () => factoryBlueprints.find((item) => item.blueprint_key === factoryBlueprintKey) ?? null,
    [factoryBlueprintKey, factoryBlueprints],
  );

  function factoryDefaults(blueprint: ApplicationBlueprint | null) {
    const properties = blueprint?.parameter_schema?.properties ?? {};
    const required = new Set((blueprint?.parameter_schema?.required ?? []).map((item) => item.toUpperCase()));
    const output: Record<string, unknown> = {};
    for (const [rawKey,spec] of Object.entries(properties)) {
      const key = rawKey.toUpperCase();
      if (spec.default !== undefined) {
        output[key] = spec.default;
      } else if (required.has(key) && spec.type === "object") {
        output[key] = {};
      } else if (required.has(key) && spec.type === "array") {
        output[key] = [];
      }
    }
    return JSON.stringify(output,null,2);
  }

  function selectFactoryBlueprint(value: string) {
    setFactoryBlueprintKey(value);
    const blueprint = factoryBlueprints.find((item) => item.blueprint_key === value) ?? null;
    if (blueprint) setFactoryEnvironment(blueprint.default_environment);
    setFactoryParameters(factoryDefaults(blueprint));
    setFactoryProviderOverrides("{}");
    setFactoryResourceOverrides("{}");
    setFactoryPreview(null);
  }

  const assignmentTargets = useMemo(() => {
    const resourceTargets = (overview?.resources ?? [])
      .filter((item) => !item.immutable && item.authority_class !== "ENTERPRISE_ROOT")
      .map((item) => ({
        key: "RESOURCE:" + item.resource_key,
        label: item.display_name + " · installed · " + (item.provider_key ?? "RUNYI"),
        provider: item.provider_key ?? null,
        capability: null as string | null,
        kind: "INSTALLED",
      }));

    const capabilityTargets = (overview?.marketplace ?? []).map((item) => ({
      key: "PROVIDER_CAPABILITY:" + item.provider_capability_key,
      label: item.display_name + " · " + item.provider_key + " · " + item.runyi_coverage_state,
      provider: item.provider_key,
      capability: item.logical_capability_hint ?? null,
      kind: item.installable ? "INSTALLABLE" : "CATALOGUED",
    }));

    const backendServiceTargets = (overview?.backend_services ?? []).map((item) => ({
      key: "BACKEND_SERVICE:" + item.backend_service_key,
      label: item.display_name + " · " + item.logical_role.replaceAll("_"," ") + " · " + (item.provider_key ?? "RUNYI"),
      provider: item.provider_key ?? null,
      capability: "backend.service." + item.logical_role.toLowerCase(),
      kind: "BACKEND_SERVICE",
      assignmentSlot: "BACKEND_SERVICE:" + item.logical_role,
      assignmentRole: item.backend_service_key,
    }));

    const dashboardTargets = (overview?.dashboards ?? [])
      .filter((item) =>
        item.scope_class === "APPLICATION_SCOPED"
        && (!selectedApp || item.application_entity_key === selectedApp)
      )
      .map((item) => ({
        key: "DASHBOARD:" + item.dashboard_key,
        label: item.display_name + " · dashboard · " + item.dashboard_provider_key,
        provider: null,
        capability: "dashboard.use",
        kind: "DASHBOARD",
      }));

    return [...resourceTargets, ...capabilityTargets, ...backendServiceTargets, ...dashboardTargets];
  }, [overview, selectedApp]);

  async function registerBackendService() {
    if (!backendServiceName.trim() || !backendServiceKey.trim()) return;
    const runtime = (overview?.resources ?? []).find((item) => `RESOURCE:${item.resource_key}` === backendServiceRuntime);
    const result = await controlInvoke("enterprise_register_backend_service", {
      backend_service_key: backendServiceKey.trim().toUpperCase().replace(/[^A-Z0-9_]/g,"_"),
      display_name: backendServiceName.trim(),
      logical_role: backendServiceRole,
      runtime_target_entity_key: backendServiceRuntime || null,
      provider_key: runtime?.provider_key ?? null,
      provider_project_ref: runtime?.tenant_project_ref ?? null,
      environment_key: runtime?.environment_key ?? "PRODUCTION",
      reason: "Owner registered modular application logic from New Infrastructure.",
    }, "backend_service_register");
    if (result?.backend_service) {
      setBackendServiceName("");
      setBackendServiceKey("");
      setBackendServiceRuntime("");
      setNotice("Backend Service registered with a truthful human identity. Runtime remains independently replaceable.");
    }
  }

  async function registerApplication() {
    if (!supabase || !newAppName.trim() || !newAppKey.trim()) return;
    setBusy("application");
    setNotice(null);
    try {
      const { data, error } = await supabase.functions.invoke("runyi-control-tower", {
        body: {
          operation: "enterprise_register_application",
          application_key: newAppKey.trim().toUpperCase().replace(/[^A-Z0-9_]/g, "_"),
          display_name: newAppName.trim(),
          application_class: "GENERAL",
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(String(data.error));
      setNewAppName("");
      setNewAppKey("");
      setNotice("Application registered. Infrastructure can now be assigned in Bridge.");
      await refresh();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Application registration failed.");
    } finally {
      setBusy(null);
    }
  }

  async function controlInvoke(operation: string, payload: Record<string, unknown>, label: string) {
    if (!supabase) return null;
    setBusy(label);
    setNotice(null);
    try {
      const { data, error } = await supabase.functions.invoke("runyi-control-tower", {
        body: { operation, ...payload },
      });
      if (error) throw error;
      if (data?.error) throw new Error(String(data.error));
      await refresh();
      return data;
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "RUNYI Control Tower operation failed.");
      return null;
    } finally {
      setBusy(null);
    }
  }


  async function addTargetToScopedContext(target: {
    target_entity_key: string;
    display_name: string;
    category_key: string;
    provider_key?: string | null;
    logical_capability?: string | null;
    environment_key?: string | null;
    target_kind: "CAPABILITY" | "INSTALLED";
  }) {
    if (!supabase || !selectionContext) return;
    const environment = selectionContext.environment_key || target.environment_key || "PRODUCTION";
    setBusy(`scoped:${target.target_entity_key}`);
    setNotice(null);
    try {
      if (selectionContext.application_entity_key) {
        const { data,error } = await supabase.functions.invoke("runyi-infrastructure-actions",{
          body:{
            operation:"enterprise_add_resource_assignment",
            application_entity_key:selectionContext.application_entity_key,
            target_entity_key:target.target_entity_key,
            category_key:target.category_key,
            assignment_slot:target.category_key === "BACKEND_SERVICE"
              ? `BACKEND_SERVICE:${String(target.logical_capability || "CUSTOM").replace(/^backend\.service\./,"").toUpperCase()}`
              : target.category_key,
            assignment_role:target.category_key === "BACKEND_SERVICE"
              ? target.target_entity_key.replace(/^BACKEND_SERVICE:/,"")
              : "PRIMARY",
            logical_capability:target.logical_capability ?? null,
            provider_key:target.provider_key ?? null,
            environment_key:environment,
            requested_state:"STANDBY",
            source_of_truth:false,
            shared_resource:false,
            metadata:{source:"MARKETPLACE_SCOPED_SELECTION"},
          },
        });
        if (error) throw error;
        if (data?.error) throw new Error(String(data.error));
        setNotice(`${target.display_name} added to ${selectionContext.application_display_name || "the selected application"}. Provider infrastructure was not destroyed or recreated.`);
      } else if (selectionContext.project_id) {
        const itemKey = `MARKETPLACE:${target.target_entity_key}:${environment}:PRIMARY`;
        const { data,error } = await supabase.functions.invoke("runyi-infrastructure-projects",{
          body:{
            action:"add_resource_preference",
            project_id:selectionContext.project_id,
            item_key:itemKey,
            item:{
              item_key:itemKey,
              target_entity_key:target.target_entity_key,
              display_name:target.display_name,
              category_key:target.category_key,
              provider_key:target.provider_key ?? null,
              assignment_role:"PRIMARY",
              environment_key:environment,
              state:"DESIRED",
              target_kind:target.target_kind,
              logical_capability:target.logical_capability ?? null,
            },
            reason:"Owner added infrastructure from the scoped Marketplace workspace.",
          },
        });
        if (error) throw error;
        if (data?.error) throw new Error(String(data.detail ?? data.error));
        setNotice(`${target.display_name} added to ${selectionContext.project_display_name || "the selected Infrastructure Project"}. Provider infrastructure was not mutated.`);
      }
      await refresh();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Scoped infrastructure assignment failed.");
    } finally {
      setBusy(null);
    }
  }

  async function addSelectedInfrastructureToApplication(application: ApplicationItem) {
    if (!supabase || !selectionContext || !scopedTargetEntityKey) return;
    const categoryKey = selectionContext.category_key || "FUTURE_RESOURCE";
    setBusy(`scoped-app:${application.entity_key}`);
    setNotice(null);
    try {
      const { data,error } = await supabase.functions.invoke("runyi-infrastructure-actions",{
        body:{
          operation:"enterprise_add_resource_assignment",
          application_entity_key:application.entity_key,
          target_entity_key:scopedTargetEntityKey,
          category_key:categoryKey,
          assignment_slot:categoryKey === "BACKEND_SERVICE"
            ? `BACKEND_SERVICE:${String(selectionContext.logical_capability || "CUSTOM").replace(/^backend\.service\./,"").toUpperCase()}`
            : categoryKey,
          assignment_role:categoryKey === "BACKEND_SERVICE"
            ? scopedTargetEntityKey.replace(/^BACKEND_SERVICE:/,"")
            : "PRIMARY",
          logical_capability:selectionContext.logical_capability ?? null,
          provider_key:selectionContext.provider_key ?? null,
          environment_key:selectionContext.environment_key || "PRODUCTION",
          requested_state:"STANDBY",
          source_of_truth:false,
          shared_resource:false,
          metadata:{source:"INFRASTRUCTURE_SCOPED_APPLICATION_SELECTION"},
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(String(data.error));
      setNotice(`Infrastructure added to ${application.display_name}. The scoped infrastructure remains selected until Back returns to the originating Marketplace screen.`);
      await refresh();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Application assignment failed.");
    } finally {
      setBusy(null);
    }
  }

  async function previewFactory() {
    if (!factoryBlueprintKey || !factoryAppName.trim() || !factoryAppKey.trim()) return;
    try {
      const parameters = JSON.parse(factoryParameters || "{}");
      const providerOverrides = JSON.parse(factoryProviderOverrides || "{}");
      const resourceOverrides = JSON.parse(factoryResourceOverrides || "{}");
      const data = await controlInvoke("enterprise_factory_preview", {
        blueprint_key: factoryBlueprintKey,
        blueprint_revision: selectedFactoryBlueprint?.revision ?? null,
        application_key: factoryAppKey.trim().toUpperCase().replace(/[^A-Z0-9_]/g,"_"),
        display_name: factoryAppName.trim(),
        environment_key: factoryEnvironment,
        tenant_project_ref: factoryTenantProjectRef.trim() || null,
        parameters,
        provider_overrides: providerOverrides,
        resource_overrides: resourceOverrides,
      }, "factory_preview");
      const preview = data?.factory_preview && typeof data.factory_preview === "object"
        ? data.factory_preview as FactoryPreview
        : null;
      setFactoryPreview(preview);
      if (preview) {
        setNotice(
          preview.readiness === "READY"
            ? "Factory preview is READY. v1.12 creation will also publish an immutable infrastructure composition while preserving all nested M4, cost and provider approvals."
            : preview.readiness === "PARTIAL"
              ? "Factory preview is PARTIAL. Review authorization/optional readiness before creation."
              : "Factory preview is BLOCKED. Resolve required infrastructure or overrides before creation.",
        );
      }
    } catch {
      setNotice("Factory parameters and override fields must be valid JSON.");
    }
  }

  async function createFactoryApplication() {
    if (!factoryBlueprintKey || !factoryAppName.trim() || !factoryAppKey.trim()) return;
    try {
      const parameters = JSON.parse(factoryParameters || "{}");
      const providerOverrides = JSON.parse(factoryProviderOverrides || "{}");
      const resourceOverrides = JSON.parse(factoryResourceOverrides || "{}");
      const data = await controlInvoke("enterprise_factory_create", {
        blueprint_key: factoryBlueprintKey,
        blueprint_revision: selectedFactoryBlueprint?.revision ?? null,
        application_key: factoryAppKey.trim().toUpperCase().replace(/[^A-Z0-9_]/g,"_"),
        display_name: factoryAppName.trim(),
        environment_key: factoryEnvironment,
        tenant_project_ref: factoryTenantProjectRef.trim() || null,
        parameters,
        provider_overrides: providerOverrides,
        resource_overrides: resourceOverrides,
        allow_blocked: false,
        application_approval_id: factoryApplicationApprovalId.trim() || null,
        manifest_approval_id: factoryManifestApprovalId.trim() || null,
        desired_state_approval_id: factoryDesiredApprovalId.trim() || null,
        composition_approval_id: factoryCompositionApprovalId.trim() || null,
      }, "factory_create");
      const run = data?.factory_run;
      if (run?.status === "AUTHORIZATION_REQUIRED") {
        setNotice("Factory composition is waiting for one or more bound Owner approvals. Add the approval references shown by your governed approval workflow and retry; RUNYI will reuse the same idempotent Factory run.");
      } else if (run?.status === "COMPOSED") {
        setNotice("Application Factory composition completed. The app now has an immutable infrastructure composition plus its initial release in Flow for governed provisioning, verification and activation.");
        setFactoryPreview(null);
        setFactoryApplicationApprovalId("");
        setFactoryManifestApprovalId("");
        setFactoryDesiredApprovalId("");
        setFactoryCompositionApprovalId("");
      } else if (run?.status === "BLOCKED") {
        setNotice("Factory creation remains BLOCKED by a required infrastructure path.");
      } else if (run) {
        setNotice("Factory run state: " + String(run.status ?? "UNKNOWN"));
      }
    } catch {
      setNotice("Factory parameters and override fields must be valid JSON.");
    }
  }

  async function publishCustomBlueprint() {
    if (!customBlueprintKey.trim() || !customBlueprintName.trim() || !customBlueprintVersion.trim()) return;
    try {
      const parameterSchema = JSON.parse(customBlueprintSchema || "{}");
      const stackTemplate = JSON.parse(customBlueprintStack || "{}");
      const data = await controlInvoke("enterprise_blueprint_publish", {
        blueprint_key: customBlueprintKey.trim().toUpperCase().replace(/[^A-Z0-9_]/g,"_"),
        display_name: customBlueprintName.trim(),
        version: customBlueprintVersion.trim(),
        application_class: customBlueprintClass.trim().toUpperCase() || "GENERAL",
        category: customBlueprintCategory.trim().toUpperCase() || "CUSTOM",
        parameter_schema: parameterSchema,
        stack_template: stackTemplate,
        default_environment: "DEV",
        approval_id: customBlueprintApprovalId.trim() || null,
      }, "blueprint_publish");
      if (data?.blueprint?.blueprint_id) {
        setNotice("Custom application blueprint revision published. It is immutable and now available in the Factory.");
        setCustomBlueprintApprovalId("");
      }
    } catch {
      setNotice("Custom blueprint parameter schema and stack template must be valid JSON.");
    }
  }

  async function publishStackManifest() {
    if (!stackApp || !stackManifestKey.trim() || !stackVersion.trim()) return;
    try {
      const parsed = JSON.parse(stackManifestJson || "{}");
      const data = await controlInvoke("enterprise_stack_publish", {
        application_entity_key: stackApp,
        manifest_key: stackManifestKey.trim(),
        version: stackVersion.trim(),
        tenant_project_ref: stackTenantProjectRef.trim() || null,
        stack_manifest: parsed,
      }, "stack_publish");
      const manifestId = String(data?.stack_manifest?.manifest_id ?? "");
      if (manifestId) {
        setPublishedManifestId(manifestId);
        setNotice("Application stack manifest published. Compose it into an environment release when ready.");
      }
    } catch {
      setNotice("Application stack manifest must be valid JSON.");
    }
  }

  async function composeStackRelease(manifestId?: string) {
    const id = manifestId || publishedManifestId;
    if (!id) {
      setNotice("Publish or select an application stack manifest first.");
      return;
    }
    const data = await controlInvoke("enterprise_release_compose", {
      manifest_id: id,
      environment_key: stackEnvironment,
      tenant_project_ref: stackTenantProjectRef.trim() || null,
      provider_preferences: {},
      policy_constraints: {},
    }, "release_compose");
    if (data?.release?.release_id) {
      setNotice("Application release composed. Review its plan in Flow before execution.");
    }
  }

  async function releaseAction(
    operation: string,
    payload: Record<string, unknown>,
    label: string,
    success: string,
  ) {
    const data = await controlInvoke(operation, payload, label);
    if (data) setNotice(success);
  }

  async function checkSupabaseScopes() {
    const projectRef = scopeProjectRef.trim();
    if (!projectRef) return;
    const data = await controlInvoke("enterprise_supabase_scope_readiness", {
      tenant_project_ref: projectRef,
    }, "scope_readiness");
    const readiness = data?.supabase_oauth && typeof data.supabase_oauth === "object"
      ? data.supabase_oauth as Record<string, unknown>
      : null;
    setScopeReadiness(readiness);
    if (readiness) {
      setNotice(readiness.reauthorization_required
        ? "Supabase reauthorization is required before the missing Management API scopes can be used."
        : "Supabase Management OAuth scopes are ready.");
    }
  }

  async function createDashboard() {
    if (!supabase || !dashboardApp || !dashboardName.trim() || !dashboardKey.trim()) return;
    setBusy("dashboard");
    setNotice(null);
    try {
      const { data, error } = await supabase.functions.invoke("runyi-control-tower", {
        body: {
          operation: "enterprise_create_application_dashboard",
          application_entity_key: dashboardApp,
          dashboard_key: dashboardKey.trim().toUpperCase().replace(/[^A-Z0-9_]/g, "_"),
          display_name: dashboardName.trim(),
          dashboard_provider_key: dashboardProvider,
          dashboard_role: "OPERATIONS",
          is_primary: false,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(String(data.error));
      setDashboardName("");
      setDashboardKey("");
      setNotice("Application-scoped dashboard created. It does not inherit Enterprise Control Tower authority.");
      await refresh();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Dashboard creation failed.");
    } finally {
      setBusy(null);
    }
  }

  async function saveAssignment() {
    if (!supabase || !selectedApp || !selectedTarget || !selectedSlot) return;
    const target = assignmentTargets.find((item) => item.key === selectedTarget);
    if (!target) return;

    setBusy("assignment");
    setNotice(null);
    try {
      const { data, error } = await supabase.functions.invoke("runyi-control-tower", {
        body: {
          operation: "enterprise_set_resource_assignment",
          application_entity_key: selectedApp,
          assignment_slot: String((target as any).assignmentSlot ?? selectedSlot),
          assignment_role: String((target as any).assignmentRole ?? "PRIMARY"),
          target_entity_key: selectedTarget,
          logical_capability: target.capability,
          provider_key: target.provider,
          environment_key: "PRODUCTION",
          source_of_truth: selectedSlot === "PRIMARY_DATABASE",
          shared_resource: false,
          metadata: target.provider === "SUPABASE" && tenantProjectRef.trim()
            ? { tenant_project_ref: tenantProjectRef.trim() }
            : {},
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(String(data.error));
      setNotice(
        target.kind === "INSTALLED" || target.kind === "DASHBOARD" || target.kind === "BACKEND_SERVICE"
          ? "Resource assignment saved."
          : "Desired infrastructure selection saved. Provisioning remains separately governed.",
      );
      setTenantProjectRef("");
      await refresh();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Resource assignment failed.");
    } finally {
      setBusy(null);
    }
  }

  function initializeFlowAssignment(assignmentId: string) {
    setFlowAssignmentId(assignmentId);
    const assignment = (overview?.assignments ?? []).find((item) => item.assignment_id === assignmentId);
    const capabilityKey = String(assignment?.target_entity_key ?? "").replace("PROVIDER_CAPABILITY:", "");
    const applicationKey = String(assignment?.application_entity_key ?? "APPLICATION").replace("APPLICATION:", "");
    const suffix = assignmentId.slice(0, 8).toLowerCase();

    let providerPayload: Record<string, unknown> = {};
    let resourceType = "RESOURCE";
    let displayName = capabilityKey || "RUNYI Infrastructure";

    if (capabilityKey === "cloudflare.d1") {
      providerPayload = { name: ("runyi-" + applicationKey + "-" + suffix).toLowerCase().replace(/[^a-z0-9-]/g, "-") };
      resourceType = "DATABASE";
      displayName = "Cloudflare D1 Database";
    } else if (capabilityKey === "cloudflare.kv") {
      providerPayload = { title: "RUNYI " + applicationKey + " " + suffix };
      resourceType = "KV_NAMESPACE";
      displayName = "Cloudflare KV Namespace";
    } else if (capabilityKey === "cloudflare.queues") {
      providerPayload = { queue_name: ("runyi-" + applicationKey + "-" + suffix).toLowerCase().replace(/[^a-z0-9-]/g, "-") };
      resourceType = "QUEUE";
      displayName = "Cloudflare Queue";
    } else if (capabilityKey === "cloudflare.workers") {
      providerPayload = {
        script_name: ("runyi-" + applicationKey + "-" + suffix).toLowerCase().replace(/[^a-z0-9_-]/g, "-"),
        main_module: "index.js",
        script_content: "export default { async fetch() { return new Response('RUNYI'); } };",
      };
      resourceType = "FUNCTION";
      displayName = "Cloudflare Worker";
    } else if (capabilityKey === "cloudflare.dns") {
      providerPayload = { zone_id: "", type: "CNAME", name: "", content: "", ttl: 1, proxied: true };
      resourceType = "DNS_RECORD";
      displayName = "Cloudflare DNS Record";
    } else if (capabilityKey === "firebase.firestore") {
      providerPayload = { database_id: ("runyi-" + suffix), location_id: "", database_type: "FIRESTORE_NATIVE" };
      resourceType = "DATABASE";
      displayName = "Firebase Firestore Database";
    } else if (capabilityKey === "supabase.auth") {
      providerPayload = { config: {} };
      resourceType = "IDENTITY";
      displayName = "Supabase Auth Configuration";
    } else if (capabilityKey === "supabase.pgvector") {
      providerPayload = {};
      resourceType = "VECTOR_DATABASE";
      displayName = "Supabase pgvector";
    }

    setFlowProviderPayload(JSON.stringify(providerPayload, null, 2));
    setFlowResourceSpec(JSON.stringify({
      resource_key: ("APP:" + applicationKey + ":" + capabilityKey + ":" + suffix).toUpperCase(),
      resource_type: resourceType,
      display_name: displayName,
      authority_class: "RUNYI_CONTROLLED",
      data_authority: assignment?.source_of_truth ? "SOURCE_OF_TRUTH" : "NOT_SOURCE_OF_TRUTH",
      tenant_project_ref: assignment?.metadata?.tenant_project_ref ?? undefined,
    }, null, 2));
  }

  async function flowInvoke(operation: string, payload: Record<string, unknown>, label: string) {
    if (!supabase) return null;
    setBusy(label);
    setNotice(null);
    try {
      const { data, error } = await supabase.functions.invoke("runyi-control-tower", {
        body: { operation, ...payload },
      });
      if (error) throw error;
      if (data?.error) throw new Error(String(data.error));
      setNotice(label.replaceAll("_", " ") + " completed.");
      await refresh();
      return data?.flow ?? data;
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "RUNYI Flow operation failed.");
      return null;
    } finally {
      setBusy(null);
    }
  }

  async function prepareFlowAssignment() {
    if (!flowAssignmentId) return;
    try {
      const providerPayload = JSON.parse(flowProviderPayload || "{}");
      const resourceSpec = JSON.parse(flowResourceSpec || "{}");
      await flowInvoke("enterprise_flow_prepare_assignment", {
        assignment_id: flowAssignmentId,
        provider_payload: providerPayload,
        resource_spec: resourceSpec,
        cost_confirmation_ref: flowCostRef.trim() || null,
      }, "flow_prepare");
    } catch {
      setNotice("Provider payload and resource specification must be valid JSON.");
    }
  }

  function resolveMarketplaceIntent() {
    const intent = marketplaceIntent.trim();
    if (!intent) return;
    setInfrastructureSection("marketplace");
    setSearch(intent);
    setNotice("RUNYI is resolving this intent against the live Marketplace. The selected capability continues through the same governed execution, verification and reconciliation lane.");
  }

  if (!connected) {
    return <EmptyState text="Sign in to RUNYI to open the Enterprise Control Tower." />;
  }

  const [title, subtitle] = titleForMode(mode);

  return (
    <div className="w-full min-w-0 max-w-full space-y-4">
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-primary">RUNYI Enterprise Control Tower</p>
          <h1 className="mt-0.5 text-xl font-semibold tracking-tight sm:text-3xl">{title}</h1>
          <p className="mt-1 max-w-3xl text-xs leading-5 text-foreground/60 sm:text-sm sm:leading-6">{subtitle}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="hidden text-[10px] text-foreground/45 sm:inline">
            {valueSync.state === "CURRENT" && lastUpdatedAt ? `Synchronized · ${lastUpdatedAt}` : valueSync.state === "SYNCING" ? "Syncing…" : valueSync.state === "ERROR" ? "Sync error" : "Checking state…"}
          </span>
          {valueSync.state === "ERROR" && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="size-9 rounded-xl"
              aria-label="Retry state recovery"
              title="Retry state recovery"
              onClick={() => void refresh()}
              disabled={loading}
            >
              {loading ? <LoaderCircle className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}
            </Button>
          )}
        </div>
      </div>

      {mode === "infrastructure" ? (
        <section className="rounded-2xl border border-foreground/8 bg-card p-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-foreground/42">Automation status</p>
              <p className="mt-1 text-sm font-semibold">
                {valueSync.state === "CURRENT" ? "● RUNYI READY" : valueSync.state === "ERROR" ? "Recovery required" : "RUNYI synchronizing"}
              </p>
              <p className="mt-1 text-[10px] leading-4 text-foreground/48">
                One Handshake · one resolution plane · one governed execution lane · automatic verification and reconciliation.
              </p>
            </div>
            <Badge className={tone(valueSync.state === "CURRENT" ? "CURRENT" : valueSync.state === "ERROR" ? "ERROR" : "SYNCING")}>
              {valueSync.state}
            </Badge>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-7">
            <Metric label="Applications" value={fastSummary?.summary?.applications ?? overview?.summary?.applications} />
            <Metric label="Backend logic" value={fastSummary?.summary?.backend_services ?? overview?.summary?.backend_services} />
            <Metric label="Capabilities" value={fastSummary?.summary?.provider_capabilities ?? overview?.summary?.provider_capabilities} />
            <Metric label="Installed" value={fastSummary?.summary?.installed_resources ?? overview?.summary?.installed_resources} />
            <Metric label="Assignments" value={fastSummary?.summary?.assignments ?? ((overview?.summary?.active_assignments ?? 0) + (overview?.summary?.desired_assignments ?? 0))} />
            <Metric label="Sync current" value={fastSummary?.summary?.sync_current ?? overview?.summary?.sync_current} />
            <Metric label="Architecture" value={fastSummary?.architecture_version ? "v" + fastSummary.architecture_version : overview?.architecture_version ? "v" + overview.architecture_version : undefined} />
          </div>
        </section>
      ) : (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-7">
          <Metric label="Applications" value={fastSummary?.summary?.applications ?? overview?.summary?.applications} />
          <Metric label="Backend logic" value={fastSummary?.summary?.backend_services ?? overview?.summary?.backend_services} />
          <Metric label="Capabilities" value={fastSummary?.summary?.provider_capabilities ?? overview?.summary?.provider_capabilities} />
          <Metric label="Installed" value={fastSummary?.summary?.installed_resources ?? overview?.summary?.installed_resources} />
          <Metric label="Assignments" value={fastSummary?.summary?.assignments ?? ((overview?.summary?.active_assignments ?? 0) + (overview?.summary?.desired_assignments ?? 0))} />
          <Metric label="Sync current" value={fastSummary?.summary?.sync_current ?? overview?.summary?.sync_current} />
          <Metric label="Architecture" value={fastSummary?.architecture_version ? "v" + fastSummary.architecture_version : overview?.architecture_version ? "v" + overview.architecture_version : undefined} />
        </div>
      )}

      {mode === "new" && (
        <div className="grid gap-4 xl:grid-cols-2">
          <div className="xl:col-span-2">
            <Panel icon={ServerCog} title="Backend Service" description="Register modular application logic separately from the runtime that executes it. RUNYI assigns a truthful human email identity and keeps the service synchronized automatically.">
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                <Input value={backendServiceName} onChange={(event) => { setBackendServiceName(event.target.value); if (!backendServiceKey) setBackendServiceKey(event.target.value.toUpperCase().replace(/[^A-Z0-9]+/g,"_").replace(/^_+|_+$/g,"")); }} placeholder="Service name" />
                <Input value={backendServiceKey} onChange={(event) => setBackendServiceKey(event.target.value.toUpperCase().replace(/[^A-Z0-9_]/g,"_"))} placeholder="SERVICE_KEY" className="font-mono" />
                <Select value={backendServiceRole} onValueChange={(value) => setBackendServiceRole(value as BackendServiceItem["logical_role"])}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["API","BUSINESS_LOGIC","EVENT_PROCESSOR","BACKGROUND_JOB","SCHEDULED_JOB","WEBHOOK","REALTIME","NOTIFICATION","AI_ORCHESTRATION","INTEGRATION","CUSTOM"].map((role) => <SelectItem key={role} value={role}>{role.replaceAll("_"," ")}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={backendServiceRuntime || "__NONE__"} onValueChange={(value) => setBackendServiceRuntime(value === "__NONE__" ? "" : value)}>
                  <SelectTrigger><SelectValue placeholder="Execution runtime" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__NONE__">Runtime later</SelectItem>
                    {(overview?.resources ?? []).filter((item) => categoryForResourceType(item.resource_type) === "RUNTIME").map((item) => (
                      <SelectItem key={item.resource_key} value={`RESOURCE:${item.resource_key}`}>{item.display_name} · {item.provider_key ?? "RUNYI"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button onClick={() => void registerBackendService()} disabled={busy === "backend_service_register" || !backendServiceName.trim() || !backendServiceKey.trim()}>
                  {busy === "backend_service_register" ? <LoaderCircle className="size-4 animate-spin" /> : <Plus className="size-4" />}
                  Register Backend Service
                </Button>
                <p className="text-[10px] text-foreground/45">Backend Service = logic identity · Runtime = replaceable execution infrastructure.</p>
              </div>
            </Panel>
          </div>

          <div className="xl:col-span-2">
            <Panel icon={Boxes} title="RUNYI Application Factory" description="Choose a reusable blueprint, preview the complete application stack, then create the application and initial release candidate through the existing governed lifecycle.">
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
                <Select value={factoryBlueprintKey} onValueChange={selectFactoryBlueprint}>
                  <SelectTrigger><SelectValue placeholder="Application blueprint" /></SelectTrigger>
                  <SelectContent>
                    {factoryBlueprints.map((blueprint) => (
                      <SelectItem key={blueprint.blueprint_id} value={blueprint.blueprint_key}>
                        {blueprint.display_name} · r{blueprint.revision}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={factoryAppName}
                  onChange={(e) => {
                    setFactoryAppName(e.target.value);
                    if (!factoryAppKey.trim()) {
                      setFactoryAppKey(e.target.value.toUpperCase().replace(/[^A-Z0-9]+/g,"_").replace(/^_+|_+$/g,""));
                    }
                    setFactoryPreview(null);
                  }}
                  placeholder="Application name"
                />
                <Input
                  value={factoryAppKey}
                  onChange={(e) => { setFactoryAppKey(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g,"_")); setFactoryPreview(null); }}
                  placeholder="APPLICATION_KEY"
                  className="font-mono"
                />
                <Select value={factoryEnvironment} onValueChange={(value) => { setFactoryEnvironment(value as typeof factoryEnvironment); setFactoryPreview(null); }}>
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(overview?.environments ?? []).map((env) => (
                      <SelectItem key={env.environment_key} value={env.environment_key}>{env.display_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={factoryTenantProjectRef}
                  onChange={(e) => { setFactoryTenantProjectRef(e.target.value); setFactoryPreview(null); }}
                  placeholder="Member project ref (optional)"
                  className="font-mono"
                />
              </div>

              {selectedFactoryBlueprint && (
                <div className="mt-3 rounded-xl bg-foreground/[0.025] p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className="bg-primary/[0.08] text-primary">{selectedFactoryBlueprint.category}</Badge>
                    <Badge className="bg-foreground/[0.05] text-foreground/55">{selectedFactoryBlueprint.application_class}</Badge>
                    <Badge className="bg-foreground/[0.05] text-foreground/55">v{selectedFactoryBlueprint.version}</Badge>
                  </div>
                  <p className="mt-2 text-xs leading-5 text-foreground/48">{selectedFactoryBlueprint.description}</p>
                </div>
              )}

              <div className="mt-3 grid gap-3 lg:grid-cols-3">
                <div>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-foreground/38">Parameters</p>
                  <Textarea
                    value={factoryParameters}
                    onChange={(e) => { setFactoryParameters(e.target.value); setFactoryPreview(null); }}
                    className="min-h-44 font-mono text-[11px]"
                  />
                </div>
                <div>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-foreground/38">Provider overrides</p>
                  <Textarea
                    value={factoryProviderOverrides}
                    onChange={(e) => { setFactoryProviderOverrides(e.target.value); setFactoryPreview(null); }}
                    className="min-h-44 font-mono text-[11px]"
                    placeholder={'{"PRIMARY_DATABASE":"CLOUDFLARE"}'}
                  />
                </div>
                <div>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-foreground/38">Existing resource overrides</p>
                  <Textarea
                    value={factoryResourceOverrides}
                    onChange={(e) => { setFactoryResourceOverrides(e.target.value); setFactoryPreview(null); }}
                    className="min-h-44 font-mono text-[11px]"
                    placeholder={'{"OBJECT_STORAGE":"RESOURCE_KEY"}'}
                  />
                </div>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => void previewFactory()} disabled={busy === "factory_preview" || !factoryBlueprintKey || !factoryAppName.trim() || !factoryAppKey.trim()}>
                  {busy === "factory_preview" ? <LoaderCircle className="size-4 animate-spin" /> : <Gauge className="size-4" />}
                  Preview application
                </Button>
                <Button onClick={() => void createFactoryApplication()} disabled={busy === "factory_create" || !factoryPreview || factoryPreview.readiness === "BLOCKED"}>
                  {busy === "factory_create" ? <LoaderCircle className="size-4 animate-spin" /> : <Plus className="size-4" />}
                  Create application
                </Button>
                {factoryPreview && <Badge className={tone(factoryPreview.readiness)}>PREVIEW {factoryPreview.readiness}</Badge>}
              </div>

              {factoryPreview && (
                <div className="mt-4 space-y-3">
                  <div className="grid gap-2 sm:grid-cols-3">
                    <Mini label="Required blocked" value={factoryPreview.required_blocked_count} />
                    <Mini label="Optional blocked" value={factoryPreview.optional_blocked_count} />
                    <Mini label="OAuth not ready" value={factoryPreview.oauth_not_ready_count} />
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                    {(factoryPreview.provider_resolution ?? []).map((item,index) => (
                      <div key={String(item.slot ?? index)} className="rounded-xl border border-foreground/8 bg-background/55 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-[10px] font-semibold">{String(item.slot ?? "RESOURCE").replaceAll("_"," ")}</p>
                          <Badge className={tone(String(item.status ?? ""))}>{String(item.status ?? "UNKNOWN")}</Badge>
                        </div>
                        <p className="mt-1 text-[10px] text-foreground/45">{String(item.provider_key ?? "No provider resolved")} · {String(item.resource_type ?? "")}</p>
                        <p className="mt-1 truncate font-mono text-[9px] text-foreground/32">{String(item.blueprint_key ?? item.resource_key ?? item.reason ?? "")}</p>
                        {item.oauth_ready === false && <p className="mt-1 text-[9px] text-amber-600 dark:text-amber-200">Provider authorization must be refreshed before execution.</p>}
                      </div>
                    ))}
                  </div>

                  <div className="rounded-xl border border-foreground/8 bg-foreground/[0.02] p-3">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-foreground/42">Bound approval references — only when required</p>
                    <p className="mt-1 text-[10px] leading-5 text-foreground/38">The Factory cannot create approvals. Paste references issued by the governed Owner approval workflow and retry the same Factory request; idempotency prevents duplicate app/release creation.</p>
                    <div className="mt-2 grid gap-2 lg:grid-cols-4">
                      <Input value={factoryApplicationApprovalId} onChange={(e) => setFactoryApplicationApprovalId(e.target.value)} placeholder="Application registration approval UUID" className="font-mono text-xs" />
                      <Input value={factoryCompositionApprovalId} onChange={(e) => setFactoryCompositionApprovalId(e.target.value)} placeholder="Infrastructure composition approval UUID" className="font-mono text-xs" />
                      <Input value={factoryManifestApprovalId} onChange={(e) => setFactoryManifestApprovalId(e.target.value)} placeholder="Manifest approval UUID" className="font-mono text-xs" />
                      <Input value={factoryDesiredApprovalId} onChange={(e) => setFactoryDesiredApprovalId(e.target.value)} placeholder="Desired-state approval UUID" className="font-mono text-xs" />
                    </div>
                  </div>
                </div>
              )}
            </Panel>
          </div>

          <Panel icon={AppWindow} title="Advanced: manual application registration" description="Register only the application identity, then configure infrastructure manually through Bridge / Flow.">
            <div className="grid gap-2 sm:grid-cols-2">
              <Input value={newAppName} onChange={(e) => setNewAppName(e.target.value)} placeholder="Application name" />
              <Input value={newAppKey} onChange={(e) => setNewAppKey(e.target.value)} placeholder="APPLICATION_KEY" className="font-mono" />
            </div>
            <Button className="mt-3" onClick={() => void registerApplication()} disabled={busy === "application" || !newAppName.trim() || !newAppKey.trim()}>
              {busy === "application" ? <LoaderCircle className="size-4 animate-spin" /> : <Plus className="size-4" />}
              Register application
            </Button>
          </Panel>

          <div className="xl:col-span-2">
            <Panel icon={Workflow} title="Custom Factory Blueprint" description="Publish an immutable custom blueprint revision. Publication defines a reusable template only; it does not create an application or execute infrastructure.">
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
                <Input value={customBlueprintName} onChange={(e) => {
                  setCustomBlueprintName(e.target.value);
                  if (!customBlueprintKey.trim()) setCustomBlueprintKey(e.target.value.toUpperCase().replace(/[^A-Z0-9]+/g,"_").replace(/^_+|_+$/g,""));
                }} placeholder="Blueprint name" />
                <Input value={customBlueprintKey} onChange={(e) => setCustomBlueprintKey(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g,"_"))} placeholder="BLUEPRINT_KEY" className="font-mono" />
                <Input value={customBlueprintVersion} onChange={(e) => setCustomBlueprintVersion(e.target.value)} placeholder="Version" className="font-mono" />
                <Input value={customBlueprintClass} onChange={(e) => setCustomBlueprintClass(e.target.value.toUpperCase())} placeholder="Application class" className="font-mono" />
                <Input value={customBlueprintCategory} onChange={(e) => setCustomBlueprintCategory(e.target.value.toUpperCase())} placeholder="Category" className="font-mono" />
              </div>
              <div className="mt-3 grid gap-3 lg:grid-cols-2">
                <div>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-foreground/38">Parameter schema</p>
                  <Textarea value={customBlueprintSchema} onChange={(e) => setCustomBlueprintSchema(e.target.value)} className="min-h-56 font-mono text-[11px]" />
                </div>
                <div>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-foreground/38">Stack template</p>
                  <Textarea value={customBlueprintStack} onChange={(e) => setCustomBlueprintStack(e.target.value)} className="min-h-56 font-mono text-[11px]" />
                </div>
              </div>
              <div className="mt-3 grid gap-2 lg:grid-cols-[minmax(0,1fr)_auto]">
                <Input value={customBlueprintApprovalId} onChange={(e) => setCustomBlueprintApprovalId(e.target.value)} placeholder="Bound blueprint publication approval UUID when required" className="font-mono" />
                <Button variant="outline" onClick={() => void publishCustomBlueprint()} disabled={busy === "blueprint_publish" || !customBlueprintName.trim() || !customBlueprintKey.trim()}>
                  {busy === "blueprint_publish" ? <LoaderCircle className="size-4 animate-spin" /> : <Plus className="size-4" />}
                  Publish blueprint revision
                </Button>
              </div>
            </Panel>
          </div>

          <div className="xl:col-span-2">
            <Panel icon={Boxes} title="Advanced: Application Stack Composer" description="Declare the complete application stack once. RUNYI compiles it into existing desired-state, provisioning, deployment, dashboard and release contracts.">
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
                <Select value={stackApp} onValueChange={(value) => {
                  setStackApp(value);
                  const key = value.replace("APPLICATION:", "");
                  setStackManifestKey("application." + key.toLowerCase() + ".stack");
                  setPublishedManifestId("");
                }}>
                  <SelectTrigger><SelectValue placeholder="Choose application" /></SelectTrigger>
                  <SelectContent>
                    {(overview?.applications ?? []).map((app) => (
                      <SelectItem key={app.entity_key} value={app.entity_key}>{app.display_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input value={stackManifestKey} onChange={(e) => setStackManifestKey(e.target.value)} placeholder="Manifest key" className="font-mono" />
                <Input value={stackVersion} onChange={(e) => setStackVersion(e.target.value)} placeholder="Version" className="font-mono" />
                <Select value={stackEnvironment} onValueChange={(value) => setStackEnvironment(value as typeof stackEnvironment)}>
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(overview?.environments ?? [
                      { environment_key: "DEV", display_name: "Development", promotion_order: 10, protected: false, active: true },
                      { environment_key: "STAGING", display_name: "Staging", promotion_order: 20, protected: true, active: true },
                      { environment_key: "CANARY", display_name: "Canary", promotion_order: 30, protected: true, active: true },
                      { environment_key: "PRODUCTION", display_name: "Production", promotion_order: 40, protected: true, active: true },
                    ]).map((env) => <SelectItem key={env.environment_key} value={env.environment_key}>{env.display_name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input value={stackTenantProjectRef} onChange={(e) => setStackTenantProjectRef(e.target.value)} placeholder="Tenant project ref (optional)" className="font-mono" />
              </div>

              <div className="mt-3 grid gap-3 lg:grid-cols-[minmax(0,1fr)_320px]">
                <div>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-foreground/38">Stack manifest JSON</p>
                  <Textarea value={stackManifestJson} onChange={(e) => setStackManifestJson(e.target.value)} className="min-h-72 font-mono text-[11px]" />
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-foreground/38">Existing stack manifests</p>
                    <Select value={publishedManifestId} onValueChange={setPublishedManifestId}>
                      <SelectTrigger><SelectValue placeholder="Select published manifest" /></SelectTrigger>
                      <SelectContent>
                        {(overview?.application_manifests ?? [])
                          .filter((item) => !stackApp || item.target_entity_key === stackApp)
                          .filter((item) => item.metadata?.application_stack_manifest === true)
                          .map((item) => (
                            <SelectItem key={item.manifest_id} value={item.manifest_id}>
                              {item.manifest_key} · r{item.revision} · {item.version}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="rounded-xl bg-foreground/[0.025] p-3 text-[10px] leading-5 text-foreground/45">
                    <p><strong className="text-foreground">Compose lifecycle</strong></p>
                    <p>Stack → desired state → deterministic plan → reuse/provision → deploy → verify → immutable release.</p>
                    <p className="mt-2">Provider choices remain Owner-controlled. Unsupported capabilities compile BLOCKED rather than silently changing provider.</p>
                  </div>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <Button onClick={() => void publishStackManifest()} disabled={!stackApp || !stackManifestKey.trim() || busy === "stack_publish"}>
                  {busy === "stack_publish" ? <LoaderCircle className="size-4 animate-spin" /> : <Plus className="size-4" />}
                  Publish stack
                </Button>
                <Button variant="outline" onClick={() => void composeStackRelease()} disabled={!publishedManifestId || busy === "release_compose"}>
                  {busy === "release_compose" ? <LoaderCircle className="size-4 animate-spin" /> : <Workflow className="size-4" />}
                  Compose {stackEnvironment} release
                </Button>
              </div>
            </Panel>
          </div>

          <Panel icon={LayoutDashboard} title="New application dashboard" description="Create a dashboard scoped to one application. It cannot inherit RUNYI Enterprise Control Tower authority.">
            <div className="grid gap-2 sm:grid-cols-2">
              <Select value={dashboardApp} onValueChange={setDashboardApp}>
                <SelectTrigger><SelectValue placeholder="Choose application" /></SelectTrigger>
                <SelectContent>
                  {(overview?.applications ?? []).map((app) => <SelectItem key={app.entity_key} value={app.entity_key}>{app.display_name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={dashboardProvider} onValueChange={setDashboardProvider}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(overview?.dashboard_providers ?? []).map((item) => <SelectItem key={item.dashboard_provider_key} value={item.dashboard_provider_key}>{item.display_name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input value={dashboardName} onChange={(e) => setDashboardName(e.target.value)} placeholder="Dashboard name" />
              <Input value={dashboardKey} onChange={(e) => setDashboardKey(e.target.value)} placeholder="DASHBOARD_KEY" className="font-mono" />
            </div>
            <Button className="mt-3" onClick={() => void createDashboard()} disabled={busy === "dashboard" || !dashboardApp || !dashboardName.trim() || !dashboardKey.trim()}>
              {busy === "dashboard" ? <LoaderCircle className="size-4 animate-spin" /> : <Plus className="size-4" />}
              Create scoped dashboard
            </Button>
          </Panel>

          <Panel icon={Boxes} title="Infrastructure creation" description="Databases, storage, hosting, runtimes and services are created through Catalogue → Bridge → Flow so provider readiness, cost and approval gates remain intact.">
            <div className="flex flex-wrap gap-1.5">
              {(overview?.categories ?? []).slice(0, 18).map((item) => (
                <Badge key={item.category_key} className="bg-background/70 text-foreground/55">{item.display_name}</Badge>
              ))}
            </div>
          </Panel>

          <Panel icon={ServerCog} title="Control Tower boundary" description="RUNYI itself retains the complete enterprise dashboard. New app dashboards are always application-scoped.">
            <div className="space-y-2 text-xs text-foreground/50">
              <p>Enterprise dashboard: <strong className="text-foreground">RUNYI Enterprise Control Tower</strong></p>
              <p>Application dashboard authority: <strong className="text-foreground">single application only</strong></p>
              <p>Provisioning authority: <strong className="text-foreground">M4 + cost + provider approval</strong></p>
            </div>
          </Panel>
        </div>
      )}

      {mode === "catalogue" && (
        <>
          <Filters
            search={search}
            setSearch={setSearch}
            category={category}
            setCategory={setCategory}
            provider={provider}
            setProvider={setProvider}
            categories={overview?.categories ?? []}
            providers={providers}
          />
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {marketplace.map((item) => (
              <Card key={item.provider_capability_key} className="border-foreground/8 bg-card py-0">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold">{item.display_name}</p>
                      <p className="mt-1 text-[10px] uppercase tracking-wider text-foreground/35">{item.category_key.replaceAll("_", " ")}</p>
                    </div>
                    <div className="flex shrink-0 items-start gap-1">
                      <Badge className={tone(item.runyi_coverage_state)}>{item.runyi_coverage_state}</Badge>
                      <UniversalInfrastructureActions
                        supabase={supabase}
                        connected={connected}
                        target={{
                          target_entity_key:`PROVIDER_CAPABILITY:${item.provider_capability_key}`,
                          display_name:item.display_name,
                          category_key:item.category_key,
                          provider_key:item.provider_key,
                          logical_capability:item.logical_capability_hint ?? null,
                          target_kind:"CAPABILITY",
                          metadata:{provider_capability_key:item.provider_capability_key},
                        }}
                        applications={(overview?.applications ?? []).map((app) => ({entity_key:app.entity_key,display_name:app.display_name}))}
                        assignments={(overview?.assignments ?? []).filter((assignment) =>
                          assignment.target_entity_key === `PROVIDER_CAPABILITY:${item.provider_capability_key}`
                          || assignment.logical_capability === item.logical_capability_hint
                        )}
                        onChanged={() => void refresh()}
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    <Badge className="bg-foreground/[0.05] text-foreground/55">{item.provider_key}</Badge>
                    <Badge className={tone(item.state)}>{item.state}</Badge>
                    {item.installable && <Badge className="bg-emerald-400/10 text-emerald-500 dark:text-emerald-300">INSTALLABLE</Badge>}
                  </div>
                  <p className="mt-3 font-mono text-[10px] text-foreground/38">{item.provider_capability_key}</p>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-[10px]">
                    <Mini label="Installed" value={item.installed_resource_count} />
                    <Mini label="Assigned apps" value={item.assigned_application_count} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {mode === "applications" && (
        <div className="space-y-3">
          {selectionContext && scopedTargetEntityKey && (selectionContext.target_scope === "APPLICATION" || selectionContext.target_scope === "PROJECT") && (
            <div className="rounded-2xl border border-primary/20 bg-primary/[0.055] p-3">
              <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-primary">Scoped infrastructure selection</p>
              <p className="mt-1 text-sm font-semibold">{selectionContext.source_display_name || scopedTargetEntityKey}</p>
              <p className="mt-1 text-[10px] leading-5 text-foreground/50">
                {selectionContext.target_scope === "PROJECT" ? "Choose Infrastructure Projects for this infrastructure." : "Choose applications for this infrastructure."}
                {" "}The infrastructure remains selected until Back returns to the originating Marketplace screen.
              </p>
            </div>
          )}
          <div className="relative z-10 w-full min-w-0 rounded-2xl border border-foreground/8 bg-[var(--top-bar)]/95 p-1.5 backdrop-blur-xl">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-foreground/38" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search Applications & Projects only…"
                className="h-10 rounded-xl border-foreground/10 bg-background pl-9"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-primary/15 bg-primary/[0.035] p-2">
            <div className="min-w-0 flex-1"><p className="text-xs font-semibold">Universal Application & Project Workspace</p><p className="mt-0.5 text-[10px] text-foreground/50">Configure product requirements and Marketplace assignments; authorized AI and developers use the saved specification to build, open, test and maintain it.</p></div>
            <Button size="sm" onClick={() => { setApplicationsSection("projects"); setProjectCreateToken((token) => token + 1); }}><Plus className="size-3.5" />Create project</Button>
          </div>

          <WorkspaceTabs
            value={applicationsSection}
            onChange={(value) => { setApplicationsSection(value as typeof applicationsSection); if (value !== "projects") setProjectCreateToken(0); }}
            items={[
              { id: "apps", label: "Applications", count: finishedApplications.length },
              { id: "projects", label: "Projects", count: draftApplications.length },
              { id: "compositions", label: "Compositions" },
            ]}
          />

          {applicationsSection === "projects" && (
            <div className="space-y-2">
              <p className="text-[11px] text-foreground/55">Projects are unfinished applications. Select the same authorized Marketplace services and models, then complete the governed release process to move into Applications.</p>
              {draftApplications.map((app) => (
                <div key={app.entity_key} className="min-w-0 rounded-xl border border-foreground/10 bg-card p-3">
                  <div className="flex items-start justify-between gap-2">
                    <button type="button" className="min-w-0 flex-1 text-left" onClick={() => setExpandedApplication(expandedApplication === app.entity_key ? null : app.entity_key)}>
                      <p className="truncate text-sm font-semibold">{app.display_name}</p>
                      <p className="mt-1 break-all font-mono text-[9px] text-foreground/45">{app.entity_key}</p>
                    </button>
                    <Badge className="bg-foreground/[0.05] text-foreground/60">PROJECT</Badge>
                    <ApplicationInfrastructureMenu applicationEntityKey={app.entity_key} applicationDisplayName={app.display_name}
                      onOpen={() => setExpandedApplication(app.entity_key)} onManageInfrastructure={() => setExpandedApplication(app.entity_key)}
                      onEditConfiguration={() => setAppsConfigKey(app.entity_key)}
                      onOpenTest={() => setAppsTestTarget({ appKey: app.entity_key, projectName: null })}
                      onOpenProjects={() => setApplicationsSection("projects")} />
                  </div>
                  {expandedApplication === app.entity_key && <div className="mt-3 space-y-2 border-t border-foreground/10 pt-3">
                    <p className="text-[10px] text-foreground/50">Configure its AI provider and model; select other services from the same Marketplace. Release through the existing governed Flow.</p>
                    <UniversalAiModelPicker supabase={supabase} connected={connected} applicationEntityKey={app.entity_key} onConfigured={() => void refresh()} />
                    <p className="text-[10px] text-foreground/55">{(overview?.assignments ?? []).filter((a) => a.application_entity_key === app.entity_key).length} infrastructure assignments</p>
                  </div>}
                </div>
              ))}
              <InfrastructureProjectsDirectory
              key={projectCreateToken}
              initialCreate={projectCreateToken > 0}
              onOpenTest={(project) => setAppsTestTarget({ appKey: project.application_entity_key ?? null, projectName: project.display_name })}
              open={true}
              supabase={supabase}
              connected={connected}
              embedded={true}
              externalSearch={search}
              hideSearch={true}
              onClose={() => setApplicationsSection("apps")}
              />
            </div>
          )}

          {applicationsSection === "compositions" && (
            <InfrastructureCompositionPanel supabase={supabase} connected={connected} mode="applications" />
          )}

          {applicationsSection === "apps" && (
            <div className="space-y-2">
              {finishedApplications.length === 0 && <EmptyState text="No completed applications match this search. Continue in Projects until a verified Production release is active." />}
              {finishedApplications.map((app) => {
                const appAssignments = (overview?.assignments ?? []).filter((a) => a.application_entity_key === app.entity_key);
                const appDashboards = (overview?.dashboards ?? []).filter((d) => d.application_entity_key === app.entity_key);
                const appReleases = (overview?.application_releases ?? []).filter((r) => r.application_entity_key === app.entity_key);
                const expanded = expandedApplication === app.entity_key;
                return (
                  <Card key={app.entity_key} className="border-foreground/8 bg-card py-0 shadow-sm">
                    <CardContent className="p-3">
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          className="flex min-w-0 flex-1 items-center gap-3 text-left"
                          onClick={() => setExpandedApplication(expanded ? null : app.entity_key)}
                        >
                          <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-primary/[0.08] text-primary">
                            <AppWindow className="size-4" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-semibold">{app.display_name}</span>
                            <span className="mt-0.5 block break-all font-mono text-[9px] text-foreground/42">{app.entity_key}</span>
                          </span>
                          <Badge className={tone(app.lifecycle_state)}>{app.lifecycle_state}</Badge>
                          <ChevronDown className={`size-4 shrink-0 text-foreground/40 transition ${expanded ? "rotate-180" : ""}`} />
                        </button>
                        {selectionContext?.target_scope === "APPLICATION" && scopedTargetEntityKey && (
                          <Button size="sm" variant="outline" className="h-8 rounded-lg px-2 text-[9px]" disabled={busy === `scoped-app:${app.entity_key}`} onClick={() => void addSelectedInfrastructureToApplication(app)}>
                            {busy === `scoped-app:${app.entity_key}` ? <LoaderCircle className="size-3 animate-spin" /> : <Plus className="size-3" />}
                            Add selected
                          </Button>
                        )}
                        <ApplicationInfrastructureMenu applicationEntityKey={app.entity_key} applicationDisplayName={app.display_name} onOpen={() => setExpandedApplication(app.entity_key)} onManageInfrastructure={() => setExpandedApplication(app.entity_key)} onEditConfiguration={() => setAppsConfigKey(app.entity_key)} onOpenTest={() => setAppsTestTarget({ appKey: app.entity_key, projectName: null })} onOpenProjects={() => setApplicationsSection("projects")} />
                      </div>

                      <div className="mt-2 grid grid-cols-3 gap-1.5">
                        <Mini label="Resources" value={appAssignments.length} />
                        <Mini label="Releases" value={appReleases.length} />
                        <Mini label="Dashboards" value={appDashboards.length} />
                      </div>

                      {expanded && (
                        <div className="mt-3 border-t border-foreground/8 pt-3">
                          <p className="mb-2 text-[9px] font-semibold uppercase tracking-wider text-foreground/45">Environments</p>
                          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                            {["DEV","STAGING","CANARY","PRODUCTION"].map((env) => {
                              const current = appReleases.find((r) => r.environment_key === env && r.is_current && r.state === "ACTIVE")
                                ?? appReleases.find((r) => r.environment_key === env);
                              return (
                                <div key={env} className="rounded-xl border border-foreground/8 bg-foreground/[0.02] p-2.5">
                                  <div className="flex items-center justify-between gap-2">
                                    <p className="text-[9px] font-bold tracking-wider">{env}</p>
                                    {current ? <Badge className={tone(current.health_state)}>{current.state}</Badge> : <Badge className="bg-foreground/[0.05] text-foreground/45">NONE</Badge>}
                                  </div>
                                  {current ? (
                                    <>
                                      <p className="mt-1.5 text-[11px] font-semibold">v{current.version} · R{current.release_number}</p>
                                      <p className="mt-1 truncate font-mono text-[8px] text-foreground/42">{current.release_key}</p>
                                    </>
                                  ) : <p className="mt-1.5 text-[9px] text-foreground/45">No release</p>}
                                </div>
                              );
                            })}
                          </div>

                          <div className="mt-3 space-y-2">
                            <p className="text-[9px] font-semibold uppercase tracking-wider text-foreground/45">AI infrastructure · providers, models & capabilities</p>
                            <p className="text-[10px] text-foreground/50">Select the assigned Marketplace provider and model for this application. Chat and Work only appear inside the AI workspace.</p>
                            <UniversalAiModelPicker supabase={supabase} connected={connected}
                              applicationEntityKey={app.entity_key} onConfigured={() => void refresh()} />
                          </div>

                          <p className="mb-2 mt-3 text-[9px] font-semibold uppercase tracking-wider text-foreground/45">Assigned infrastructure</p>
                          <div className="grid gap-1.5 sm:grid-cols-2 xl:grid-cols-3">
                            {appAssignments.length === 0 && <p className="text-xs text-foreground/45">No infrastructure assignments yet.</p>}
                            {appAssignments.map((a) => {
                              const resource = (overview?.resources ?? []).find((candidate) => a.target_entity_key === `RESOURCE:${candidate.resource_key}` || a.target_entity_key === candidate.resource_key);
                              const backendService = (overview?.backend_services ?? []).find((candidate) => a.target_entity_key === `BACKEND_SERVICE:${candidate.backend_service_key}`);
                              const accountHint = resource ? providerAccountForResource(resource) : null;
                              const displayName = backendService?.display_name || resource?.display_name || a.target_entity_key;
                              const identityEmail = backendService?.identity_email || resource?.identity_email || providerAccountEmail(accountHint);
                              const providerProjectRef = backendService?.provider_project_ref || accountHint?.target_project_ref || resource?.tenant_project_ref || null;
                              const providerKey = backendService?.provider_key ?? resource?.provider_key ?? a.provider_key ?? null;
                              const syncState = backendService?.sync_state || resource?.sync_state || "UNKNOWN";
                              const lastSyncedAt = backendService?.last_synced_at || resource?.last_synced_at || null;
                              return (
                              <div key={a.assignment_id} className="rounded-xl bg-foreground/[0.025] p-2.5">
                                <div className="flex items-start justify-between gap-2">
                                  <div className="min-w-0 flex-1"><p className="truncate text-[10px] font-semibold">{displayName}</p><p className="mt-0.5 text-[8px] text-foreground/40">{a.environment_key} · {backendService ? backendService.logical_role.replaceAll("_"," ") : a.assignment_role} · {(a.category_key || a.assignment_slot).replaceAll("_", " ")}</p></div>
                                  <div className="flex shrink-0 items-start gap-1"><Badge className={tone(a.state)}>{a.state}</Badge><Badge className={tone(syncState)}>{syncState}</Badge><UniversalInfrastructureActions supabase={supabase} connected={connected} applications={overview?.applications ?? []} target={{ target_entity_key: a.target_entity_key, display_name: displayName, category_key: a.category_key || a.assignment_slot, provider_key: providerKey, logical_capability: a.logical_capability ?? null, target_kind: a.target_entity_key.startsWith("PROVIDER_CAPABILITY:") ? "CAPABILITY" : "INSTALLED", environment_key: a.environment_key, provider_account_identity:identityEmail, identity_email:identityEmail, identity_email_type:backendService?.identity_email_type || resource?.identity_email_type || null, identity_email_verified:backendService?.identity_email_verified ?? resource?.identity_email_verified ?? false, identity_email_source:backendService?.identity_email_source || resource?.identity_email_source || null, sync_state:syncState, last_synced_at:lastSyncedAt, provider_project_ref:providerProjectRef, region_ref:resource?.region_ref ?? null, lifecycle_state:backendService?.lifecycle_state || resource?.lifecycle_state || null, health_state:backendService?.health_state || resource?.health_state || null, verification_state:backendService?.verification_state || accountHint?.backend_stage || null, capabilities:resource?.capabilities ?? [] }} assignments={[a]} onChanged={() => void refresh()} /></div>
                                </div>
                                <p className="mt-1 text-[9px] text-foreground/55"><strong>Provider:</strong> {providerKey ?? "RUNYI"}</p>
                                <p className="mt-1 break-words text-[9px] text-foreground/48"><strong>Human identity:</strong> {identityEmail || "IDENTITY PENDING"}</p>
                                {backendService && <p className="mt-1 break-all text-[9px] text-foreground/48"><strong>Runtime:</strong> {backendService.runtime_target_entity_key || "Runtime pending"}</p>}
                                <p className="mt-1 break-all text-[9px] text-foreground/48"><strong>Provider project:</strong> {providerProjectRef || "Unavailable"}</p>
                                <p className="mt-1 break-all font-mono text-[8px] text-foreground/35">{a.target_entity_key}</p>
                              </div>
                              );
                            })}
                            {appDashboards.map((d) => (
                              <div key={d.dashboard_id} className="rounded-xl bg-primary/[0.035] p-2.5">
                                <div className="flex items-center justify-between gap-2">
                                  <p className="truncate text-[9px] font-semibold">{d.display_name}</p>
                                  <Badge className={tone(d.health_state)}>DASHBOARD</Badge>
                                </div>
                                <p className="mt-1 text-[9px] text-foreground/48">{d.dashboard_provider_key} · {d.dashboard_role}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {mode === "infrastructure" && (
        <div className="space-y-3">
          {(selectionContext?.application_entity_key || selectionContext?.project_id) && (
            <div className="rounded-2xl border border-primary/20 bg-primary/[0.055] p-3">
              <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-primary">Scoped Marketplace</p>
              <p className="mt-1 text-sm font-semibold">
                Adding infrastructure to {selectionContext.application_display_name || selectionContext.project_display_name || selectionContext.application_entity_key || selectionContext.project_id}
              </p>
              <p className="mt-1 text-[10px] leading-5 text-foreground/50">Every Marketplace selection is tied to this target until Back returns to the originating Applications & Projects screen.</p>
            </div>
          )}
          <section className="rounded-2xl border border-primary/15 bg-primary/[0.035] p-3">
            <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-primary">RUNYI universal front door</p>
            <p className="mt-1 text-sm font-semibold">What infrastructure should RUNYI resolve?</p>
            <p className="mt-1 text-[10px] leading-4 text-foreground/48">State the outcome. RUNYI resolves matching governed capabilities; execution remains on the same shared automation lane.</p>
            <div className="mt-3 flex min-w-0 gap-2">
              <div className="relative min-w-0 flex-1">
                <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-foreground/35" />
                <Input
                  value={marketplaceIntent}
                  onChange={(event) => setMarketplaceIntent(event.target.value)}
                  onKeyDown={(event) => { if (event.key === "Enter") resolveMarketplaceIntent(); }}
                  placeholder="Add Supabase for a member, find storage, repair drift…"
                  className="h-11 rounded-xl border-foreground/10 bg-background pl-9 pr-3 text-sm"
                />
              </div>
              <Button className="h-11 rounded-xl px-4" onClick={resolveMarketplaceIntent} disabled={!marketplaceIntent.trim()}>
                Resolve
                <ChevronRight className="size-4" />
              </Button>
            </div>
            <RunyiResponsiveActionGrid className="mt-2" minItemWidth={144}>
              <Button variant="outline" className={RUNYI_RESPONSIVE_ACTION_BUTTON_CLASS} onClick={() => { setSearch(""); setInfrastructureSection("marketplace"); }}>
                <Plus className="size-3.5 shrink-0" />
                <RunyiResponsiveActionLabel>Add infrastructure</RunyiResponsiveActionLabel>
              </Button>
              <Button variant="outline" className={RUNYI_RESPONSIVE_ACTION_BUTTON_CLASS} onClick={() => setInfrastructureSection("resources")}>
                <Database className="size-3.5 shrink-0" />
                <RunyiResponsiveActionLabel>Installed</RunyiResponsiveActionLabel>
              </Button>
              <Button variant="outline" className={RUNYI_RESPONSIVE_ACTION_BUTTON_CLASS} onClick={() => setInfrastructureSection("accounts")}>
                <ShieldCheck className="size-3.5 shrink-0" />
                <RunyiResponsiveActionLabel>Authenticate</RunyiResponsiveActionLabel>
              </Button>
              <Button variant="outline" className={RUNYI_RESPONSIVE_ACTION_BUTTON_CLASS} onClick={() => setInfrastructureSection("telemetry")}>
                <Activity className="size-3.5 shrink-0" />
                <RunyiResponsiveActionLabel>Repair / reconcile</RunyiResponsiveActionLabel>
              </Button>
            </RunyiResponsiveActionGrid>
          </section>
          <RunyiRegistryTabs
            value={infrastructureSection}
            onChange={(value) => setInfrastructureSection(value as typeof infrastructureSection)}
            entries={marketplaceUiControlPlane?.sections ?? []}
            fallbackEntries={[
              { entry_key: "FALLBACK:MARKETPLACE", section_key: "marketplace", display_name: "Marketplace", metadata: { navigation_surface: "PRIMARY" } },
              { entry_key: "FALLBACK:INSTALLED", section_key: "resources", display_name: "Installed", metadata: { navigation_surface: "PRIMARY" } },
              { entry_key: "FALLBACK:ACTIVITY", section_key: "overview", display_name: "Activity", metadata: { navigation_surface: "PRIMARY" } },
              { entry_key: "FALLBACK:TELEMETRY", section_key: "telemetry", display_name: "Telemetry", metadata: { navigation_surface: "PRIMARY" } },
            ]}
            counts={{
              marketplace: overview ? marketplace.length : fastSummary?.summary?.provider_capabilities,
              resources: overview ? resources.length : fastSummary?.summary?.installed_resources,
            }}
            elementKey="MARKETPLACE_PRIMARY_SECTIONS"
          />

          {infrastructureSection === "overview" && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <WorkspaceTile
                  icon={Boxes}
                  title="Available infrastructure"
                  meta={`${marketplace.length} marketplace capabilities`}
                  onClick={() => {
                    setSearch("");
                    setCategory("ALL");
                    setInfrastructureSection("marketplace");
                  }}
                />
                <WorkspaceTile
                  icon={Workflow}
                  title="Infrastructure projects"
                  meta="Plan independently, assign explicitly"
                  onClick={() => setInfrastructureSection("projects")}
                />
                <WorkspaceTile
                  icon={Database}
                  title="Installed resources"
                  meta={`${resources.length} registered resources`}
                  onClick={() => setInfrastructureSection("resources")}
                />
                <WorkspaceTile
                  icon={ShieldCheck}
                  title="Universal Accounts Setup"
                  meta="Authenticate, reconnect and verify provider accounts"
                  onClick={() => setInfrastructureSection("accounts")}
                />
                <WorkspaceTile
                  icon={Activity}
                  title="Provider truth"
                  meta="Verification, freshness and bindings"
                  onClick={() => setInfrastructureSection("telemetry")}
                />
                <WorkspaceTile
                  icon={Gauge}
                  title="Usage & capacity"
                  meta="Verified measurements; UNKNOWN stays unknown"
                  onClick={() => setInfrastructureSection("telemetry")}
                />
                <WorkspaceTile
                  icon={Boxes}
                  title="Pools & composition"
                  meta="Resource pools and governed compositions"
                  onClick={() => setInfrastructureSection("telemetry")}
                />
              </div>

              <section className="rounded-2xl border border-foreground/8 bg-card p-3">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold">Infrastructure categories</p>
                    <p className="text-[10px] text-foreground/45">Browse by resource family instead of scrolling through telemetry.</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                  {(overview?.categories ?? []).slice(0, 18).map((item) => {
                    const count = (overview?.marketplace ?? []).filter((entry) => entry.category_key === item.category_key).length
                      + (item.category_key === "BACKEND_SERVICE" ? (overview?.backend_services ?? []).length : 0);
                    return (
                      <button
                        type="button"
                        key={item.category_key}
                        onClick={() => {
                          setSearch("");
                          setCategory(item.category_key);
                          setInfrastructureSection("marketplace");
                        }}
                        className="flex items-center justify-between gap-2 rounded-xl border border-foreground/7 bg-foreground/[0.02] px-3 py-2.5 text-left transition hover:bg-foreground/[0.055]"
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-[10px] font-semibold">{item.display_name}</span>
                          <span className="mt-0.5 block text-[9px] text-foreground/45">{count} capabilities</span>
                        </span>
                        <ChevronRight className="size-3.5 shrink-0 text-foreground/30" />
                      </button>
                    );
                  })}
                </div>
              </section>
            </>
          )}

          {infrastructureSection === "marketplace" && (
            <>
              <Filters
                search={search}
                setSearch={setSearch}
                category={category}
                setCategory={setCategory}
                provider={provider}
                setProvider={setProvider}
                categories={overview?.categories ?? []}
                providers={providers}
              />
              {(provider === "ALL" || provider === "SUPABASE") && connectedProviderProjects.length > 0 && (
                <section className="mb-3 overflow-hidden rounded-2xl border border-foreground/8 bg-card">
                  <div className="flex items-center justify-between gap-3 border-b border-foreground/8 px-3 py-2.5">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold">Connected Supabase Projects</p>
                      <p className="mt-0.5 text-[9px] text-foreground/45">Concrete verified project infrastructure. Open a project for its live phone workspace.</p>
                    </div>
                    <Badge className="shrink-0 bg-foreground/[0.05] text-foreground/60">{connectedProviderProjects.length}</Badge>
                  </div>
                  <div className="divide-y divide-foreground/7">
                    {connectedProviderProjects.map((project) => {
                      const projectAssignments = (overview?.assignments ?? []).filter((assignment) => assignment.target_entity_key === `RESOURCE:${project.resource_key}`);
                      const components = Array.isArray(project.metadata?.component_resources) ? project.metadata.component_resources : [];
                      return (
                        <div key={project.resource_key} className="flex items-center gap-2 px-3 py-2.5">
                          <button
                            type="button"
                            className="min-w-0 flex-1 text-left"
                            onClick={() => setInfrastructureDetail({ kind: "RESOURCE", key: project.resource_key })}
                          >
                            <div className="flex items-center gap-2">
                              <span className="truncate text-[12px] font-semibold">{project.display_name}</span>
                              {project.authority_class === "ENTERPRISE_ROOT" && <Badge className="bg-primary/[0.08] text-primary">ROOT</Badge>}
                            </div>
                            <p className="mt-0.5 truncate text-[9px] text-foreground/52">{project.identity_email || "IDENTITY PENDING"}</p>
                            <p className="mt-0.5 truncate font-mono text-[8px] text-foreground/35">{project.tenant_project_ref || project.resource_key}</p>
                            <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[8px] text-foreground/46">
                              <span>{components.length} resources</span>
                              <span>{projectAssignments.length} app assignments</span>
                              <span>{project.environment_key}</span>
                            </div>
                            <div className="mt-1.5 flex flex-wrap gap-1">
                              <Badge className={`${tone(project.health_state)} max-w-[108px] truncate px-2 py-0.5 text-[8px]`}>{project.health_state}</Badge>
                              <Badge className={`${tone(project.sync_state)} max-w-[108px] truncate px-2 py-0.5 text-[8px]`}>{project.sync_state}</Badge>
                            </div>
                          </button>
                          <div className="flex shrink-0 items-center gap-1">
                            {!project.immutable && project.authority_class !== "ENTERPRISE_ROOT" ? (
                              <UniversalInfrastructureActions
                                supabase={supabase}
                                connected={connected}
                                applications={overview?.applications ?? []}
                                target={{
                                  target_entity_key: `RESOURCE:${project.resource_key}`,
                                  display_name: project.display_name,
                                  category_key: "DATABASE",
                                  provider_key: project.provider_key ?? null,
                                  target_kind: "INSTALLED",
                                  environment_key: project.environment_key,
                                  provider_account_identity: project.identity_email,
                                  identity_email: project.identity_email,
                                  identity_email_type: project.identity_email_type,
                                  identity_email_verified: project.identity_email_verified,
                                  identity_email_source: project.identity_email_source,
                                  sync_state: project.sync_state,
                                  last_synced_at: project.last_synced_at ?? null,
                                  provider_project_ref: project.tenant_project_ref ?? null,
                                  region_ref: project.region_ref ?? null,
                                  lifecycle_state: project.lifecycle_state,
                                  health_state: project.health_state,
                                  capabilities: project.capabilities ?? [],
                                }}
                                assignments={projectAssignments}
                                onChanged={() => void refresh()}
                              />
                            ) : (
                              <ChevronRight className="size-4 text-foreground/30" />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}
                            {marketplace.length === 0 && backendServices.length === 0 && <EmptyState text="No infrastructure capabilities match these filters." />}
              {backendServices.length > 0 && (
                <section className="mb-3 overflow-hidden rounded-2xl border border-foreground/8 bg-card">
                  <div className="flex items-center justify-between gap-2 border-b border-foreground/8 px-3 py-2.5">
                    <div className="min-w-0"><p className="text-xs font-semibold">Registered Backend Services</p><p className="truncate text-[9px] text-foreground/45">Application logic identities · runtime is separate and replaceable.</p></div>
                    <Badge className="shrink-0 bg-foreground/[0.05] text-foreground/55">{backendServices.length}</Badge>
                  </div>
                  <div className="divide-y divide-foreground/7">
                    {backendServices.map((item) => {
                      const assignments=(overview?.assignments ?? []).filter((a)=>a.target_entity_key===`BACKEND_SERVICE:${item.backend_service_key}`);
                      const displayIdentity=resolveBackendServiceDisplayIdentity(overview,item);
                      return (
                        <div key={item.backend_service_key} className="flex items-start gap-2 px-3 py-2.5">
                          <button type="button" className="min-w-0 flex-1 text-left" onClick={() => setInfrastructureDetail({ kind: "BACKEND_SERVICE", key: item.backend_service_key })}>
                            <p className="truncate text-[12px] font-semibold">{item.display_name}</p>
                            <p className="mt-0.5 truncate text-[9px] text-foreground/58"><span className="font-semibold">Email:</span> {displayIdentity.email} <span className="text-foreground/30">· {displayIdentitySource(displayIdentity.source)}</span></p>
                            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[8px] text-foreground/45">
                              <span>{item.logical_role.replaceAll("_"," ")}</span><span>{item.environment_key}</span><span>{assignments.length} apps</span>
                            </div>
                            <p className="mt-1 truncate font-mono text-[8px] text-foreground/32">{item.runtime_target_entity_key || "RUNTIME PENDING"}</p>
                            <div className="mt-1.5 flex flex-wrap gap-1">
                              <Badge className={`${tone(item.lifecycle_state)} max-w-[104px] truncate px-2 py-0.5 text-[8px]`}>{item.lifecycle_state}</Badge>
                              <Badge className={`${tone(item.sync_state)} max-w-[104px] truncate px-2 py-0.5 text-[8px]`}>{item.sync_state}</Badge>
                            </div>
                          </button>
                          <div className="shrink-0">
                            <UniversalInfrastructureActions supabase={supabase} connected={connected} applications={overview?.applications ?? []} target={{ target_entity_key:`BACKEND_SERVICE:${item.backend_service_key}`, display_name:item.display_name, category_key:"BACKEND_SERVICE", provider_key:item.provider_key ?? null, logical_capability:`backend.service.${item.logical_role.toLowerCase()}`, target_kind:"INSTALLED", environment_key:item.environment_key, identity_email:item.identity_email, identity_email_type:item.identity_email_type, identity_email_verified:item.identity_email_verified, identity_email_source:item.identity_email_source, provider_account_identity:displayIdentity.email === "NOT CONNECTED" ? null : displayIdentity.email, provider_project_ref:item.provider_project_ref ?? null, lifecycle_state:item.lifecycle_state, health_state:item.health_state, verification_state:item.verification_state, sync_state:item.sync_state, last_synced_at:item.last_synced_at ?? null }} assignments={assignments} onChanged={() => void refresh()} />
                          </div>
                          {(selectionContext?.application_entity_key || selectionContext?.project_id) && (
                            <Button className="h-8 shrink-0 rounded-lg px-2 text-[9px]" variant="outline" disabled={busy===`scoped:BACKEND_SERVICE:${item.backend_service_key}`} onClick={() => void addTargetToScopedContext({ target_entity_key:`BACKEND_SERVICE:${item.backend_service_key}`, display_name:item.display_name, category_key:"BACKEND_SERVICE", provider_key:item.provider_key ?? null, logical_capability:`backend.service.${item.logical_role.toLowerCase()}`, environment_key:item.environment_key, target_kind:"INSTALLED" })}>
                              <Plus className="size-3" />Add
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}
              <div className="space-y-1.5">
                {marketplace.map((item) => {
                  const capabilityAssignments = (overview?.assignments ?? []).filter((assignment) => assignment.target_entity_key === `PROVIDER_CAPABILITY:${item.provider_capability_key}`);
                  const displayIdentity = resolveCapabilityDisplayIdentity(overview,item);
                  return (
                    <div key={item.provider_capability_key} className="rounded-xl border border-foreground/8 bg-card px-3 py-2.5">
                      <div className="flex items-start gap-2">
                        <button
                          type="button"
                          className="min-w-0 flex-1 text-left"
                          onClick={() => setInfrastructureDetail({ kind: "CAPABILITY", key: item.provider_capability_key })}
                        >
                          <div className="min-w-0">
                            <span className="block truncate text-[12px] font-semibold">{item.display_name}</span>
                          </div>
                          <p className="mt-0.5 truncate text-[9px] text-foreground/58"><span className="font-semibold">Email:</span> {displayIdentity.email} <span className="text-foreground/30">· {displayIdentitySource(displayIdentity.source)}</span></p>
                          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[8px] text-foreground/48">
                            <span>{item.provider_key}</span>
                            <span>{item.category_key.replaceAll("_", " ")}</span>
                            <span>{item.installed_resource_count} installed</span>
                            <span>{item.assigned_application_count} apps</span>
                          </div>
                          <p className="mt-1 truncate font-mono text-[8px] text-foreground/32">{item.provider_capability_key}</p>
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            <Badge className={`${tone(item.state)} max-w-[104px] truncate px-2 py-0.5 text-[8px]`}>{item.state}</Badge>
                            <Badge className={`${tone(item.runyi_coverage_state)} max-w-[112px] truncate px-2 py-0.5 text-[8px]`}>{item.runyi_coverage_state}</Badge>
                          </div>
                        </button>
                        <div className="flex shrink-0 items-start gap-1">
                          <UniversalInfrastructureActions
                            supabase={supabase}
                            connected={connected}
                            applications={overview?.applications ?? []}
                            target={{
                              target_entity_key: `PROVIDER_CAPABILITY:${item.provider_capability_key}`,
                              display_name: item.display_name,
                              category_key: item.category_key,
                              provider_key: item.provider_key,
                              logical_capability: item.logical_capability_hint ?? null,
                              target_kind: "CAPABILITY",
                            }}
                            assignments={capabilityAssignments}
                            onChanged={() => void refresh()}
                          />
                        </div>
                      </div>
                      {(selectionContext?.application_entity_key || selectionContext?.project_id) && (
                        <Button className="mt-2 h-8 w-full rounded-lg text-[10px]" variant="outline" disabled={busy === `scoped:PROVIDER_CAPABILITY:${item.provider_capability_key}`} onClick={() => void addTargetToScopedContext({
                          target_entity_key:`PROVIDER_CAPABILITY:${item.provider_capability_key}`,
                          display_name:item.display_name,
                          category_key:item.category_key,
                          provider_key:item.provider_key,
                          logical_capability:item.logical_capability_hint ?? null,
                          target_kind:"CAPABILITY",
                        })}>
                          {busy === `scoped:PROVIDER_CAPABILITY:${item.provider_capability_key}` ? <LoaderCircle className="size-3.5 animate-spin" /> : <Plus className="size-3.5" />}
                          Add to {selectionContext.application_display_name || selectionContext.project_display_name || "selected target"}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {infrastructureSection === "projects" && (
            <InfrastructureProjectsDirectory
              open={true}
              supabase={supabase}
              connected={connected}
              embedded={true}
              onClose={() => setInfrastructureSection("overview")}
            />
          )}

          {infrastructureSection === "resources" && (
            <>
              <Filters
                search={search}
                setSearch={setSearch}
                category="ALL"
                setCategory={() => undefined}
                provider={provider}
                setProvider={setProvider}
                categories={[]}
                providers={providers}
                hideCategory
              />
              <div className="space-y-1.5">
                {resources.map((item) => {
                  const accountHint = providerAccountForResource(item);
                  const resolvedIdentity = resolveResourceDisplayIdentity(overview,item);
                  const providerAccountIdentity = item.identity_email || providerAccountEmail(accountHint) || (resolvedIdentity.email === "NOT CONNECTED" ? null : resolvedIdentity.email);
                  const providerProjectRef = accountHint?.target_project_ref || item.tenant_project_ref || null;
                  const resourceAssignments = (overview?.assignments ?? []).filter((assignment) => assignment.target_entity_key === `RESOURCE:${item.resource_key}`);
                  const applicationNames = Array.from(new Set(resourceAssignments.map((assignment) => (overview?.applications ?? []).find((app) => app.entity_key === assignment.application_entity_key)?.display_name ?? assignment.application_entity_key)));
                  return (
                  <div key={item.resource_key} className="flex items-start gap-2 rounded-xl border border-foreground/8 bg-card px-3 py-2.5">
                    <button type="button" className="min-w-0 flex-1 text-left" onClick={() => setInfrastructureDetail({ kind: "RESOURCE", key: item.resource_key })}>
                      <p className="truncate text-[12px] font-semibold">{item.display_name}</p>
                      <p className="mt-0.5 truncate text-[9px] text-foreground/58"><span className="font-semibold">Email:</span> {providerAccountIdentity || "NOT CONNECTED"} <span className="text-foreground/30">· {providerAccountIdentity ? (item.identity_email ? "VERIFIED OBJECT" : displayIdentitySource(resolvedIdentity.source)) : "NOT CONNECTED"}</span></p>
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[8px] text-foreground/45">
                        <span>{item.provider_key ?? "RUNYI"}</span><span>{item.resource_type}</span><span>{item.environment_key}</span><span>{applicationNames.length} apps</span>
                      </div>
                      <p className="mt-1 truncate font-mono text-[8px] text-foreground/32">{providerProjectRef || item.resource_key}</p>
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        <Badge className={`${tone(item.health_state)} max-w-[104px] truncate px-2 py-0.5 text-[8px]`}>{item.health_state}</Badge>
                        <Badge className={`${tone(item.sync_state)} max-w-[104px] truncate px-2 py-0.5 text-[8px]`}>{item.sync_state}</Badge>
                        {item.immutable && <Badge className="max-w-[112px] truncate bg-red-400/10 px-2 py-0.5 text-[8px] text-red-500">IMMUTABLE ROOT</Badge>}
                      </div>
                    </button>
                    {!item.immutable && item.authority_class !== "ENTERPRISE_ROOT" && (
                      <UniversalInfrastructureActions
                        supabase={supabase}
                        connected={connected}
                        applications={overview?.applications ?? []}
                        target={{
                          target_entity_key: `RESOURCE:${item.resource_key}`, display_name: item.display_name,
                          category_key: categoryForResourceType(item.resource_type), provider_key: item.provider_key ?? null,
                          target_kind: "INSTALLED", environment_key: item.environment_key, provider_account_identity: providerAccountIdentity,
                          identity_email:item.identity_email, identity_email_type:item.identity_email_type, identity_email_verified:item.identity_email_verified,
                          identity_email_source:item.identity_email_source, sync_state:item.sync_state, last_synced_at:item.last_synced_at ?? null,
                          provider_project_ref: providerProjectRef, region_ref:item.region_ref ?? null, lifecycle_state:item.lifecycle_state,
                          health_state:item.health_state, verification_state:accountHint?.backend_stage ?? null, capabilities:item.capabilities ?? [],
                        }}
                        assignments={resourceAssignments}
                        onChanged={() => void refresh()}
                      />
                    )}
                    {(selectionContext?.application_entity_key || selectionContext?.project_id) && !item.immutable && item.authority_class !== "ENTERPRISE_ROOT" && (
                      <Button className="h-8 shrink-0 rounded-lg px-2 text-[9px]" variant="outline" disabled={busy === `scoped:RESOURCE:${item.resource_key}`} onClick={() => void addTargetToScopedContext({ target_entity_key:`RESOURCE:${item.resource_key}`, display_name:item.display_name, category_key:categoryForResourceType(item.resource_type), provider_key:item.provider_key ?? null, environment_key:item.environment_key, target_kind:"INSTALLED" })}>
                        {busy === `scoped:RESOURCE:${item.resource_key}` ? <LoaderCircle className="size-3 animate-spin" /> : <Plus className="size-3" />}Add
                      </Button>
                    )}
                  </div>
                  );
                })}
              </div>
            </>
          )}

          {infrastructureSection === "accounts" && (
            <UniversalAccountsSetupPanel supabase={supabase} connected={connected} />
          )}

          {infrastructureSection === "telemetry" && (
            <InfrastructureCompositionPanel supabase={supabase} connected={connected} mode="infrastructure" />
          )}
        </div>
      )}

      {mode === "bridge" && (
        <div className="space-y-4">
          <InfrastructureCompositionPanel supabase={supabase} connected={connected} mode="bridge" />
          <Panel icon={Cable} title="Application Infrastructure Bridge" description="Choose installed or desired infrastructure for an application. Selecting a provider capability records desired state; it does not silently provision it.">
          <div className="grid gap-2 lg:grid-cols-3">
            <Select value={selectedApp} onValueChange={(value) => { setSelectedApp(value); setSelectedTarget(""); }}>
              <SelectTrigger><SelectValue placeholder="Choose application" /></SelectTrigger>
              <SelectContent>
                {(overview?.applications ?? []).map((app) => <SelectItem key={app.entity_key} value={app.entity_key}>{app.display_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={selectedSlot} onValueChange={(value) => { setSelectedSlot(value); setSelectedTarget(""); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ASSIGNMENT_SLOTS.map((slot) => <SelectItem key={slot} value={slot}>{slot.replaceAll("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={selectedTarget} onValueChange={setSelectedTarget}>
              <SelectTrigger><SelectValue placeholder="Choose resource / capability" /></SelectTrigger>
              <SelectContent>
                {assignmentTargets
                  .filter((item) => selectedSlot === "DASHBOARD" ? item.kind === "DASHBOARD" : item.kind !== "DASHBOARD")
                  .map((item) => <SelectItem key={item.key} value={item.key}>{item.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {assignmentTargets.find((item) => item.key === selectedTarget)?.provider === "SUPABASE" && (
            <div className="mt-3">
              <Input
                value={tenantProjectRef}
                onChange={(e) => setTenantProjectRef(e.target.value)}
                placeholder="Authorized member Supabase project ref"
                className="font-mono"
              />
              <p className="mt-1 text-[10px] text-foreground/38">Required for Supabase Auth/pgvector provisioning. OWNER_SUPABASE_ROOT is not allowed.</p>
            </div>
          )}
          <Button className="mt-3" onClick={() => void saveAssignment()} disabled={busy === "assignment" || !selectedApp || !selectedTarget}>
            {busy === "assignment" ? <LoaderCircle className="size-4 animate-spin" /> : <Cable className="size-4" />}
            Save assignment
          </Button>

          {selectedApp && (
            <div className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {(overview?.assignments ?? []).filter((a) => a.application_entity_key === selectedApp).map((a) => (
                <div key={a.assignment_id} className="rounded-xl bg-foreground/[0.025] p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[10px] font-semibold">{a.assignment_slot.replaceAll("_", " ")}</p>
                    <Badge className={tone(a.state)}>{a.state}</Badge>
                  </div>
                  <p className="mt-1 truncate font-mono text-[9px] text-foreground/38">{a.target_entity_key}</p>
                </div>
              ))}
            </div>
          )}
          </Panel>
        </div>
      )}

      {mode === "flow" && (
        <div className="space-y-3">
          <div className="relative z-10 w-full min-w-0 rounded-2xl border border-foreground/8 bg-[var(--top-bar)]/95 p-1.5 backdrop-blur-xl">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-foreground/38" />
              <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search operations, releases, deployments and IDs…" className="h-10 rounded-xl border-foreground/10 bg-background pl-9" />
            </div>
          </div>

          <WorkspaceTabs
            value={flowSection}
            onChange={(value) => setFlowSection(value as typeof flowSection)}
            items={[
              { id: "overview", label: "Overview" },
              { id: "releases", label: "Releases", count: filteredFlowReleases.length },
              { id: "authorization", label: "Authorization" },
              { id: "provisioning", label: "Provisioning", count: filteredProvisioningRequests.length },
            ]}
          />

          <div className={flowSection === "overview" ? "grid gap-3 lg:grid-cols-2" : "hidden"}>
            <Panel icon={Workflow} title="Provisioning Flow" description="DESIRED Bridge selections enter the existing governed Provisioning Execution Engine.">
              <div className="grid grid-cols-3 gap-2">
                <Mini label="Recent" value={overview?.flow?.provisioning?.total_recent ?? 0} />
                <Mini label="Pending" value={overview?.flow?.provisioning?.pending ?? 0} />
                <Mini label="Completed" value={overview?.flow?.provisioning?.completed ?? 0} />
              </div>
            </Panel>
            <Panel icon={Gauge} title="Deployment / Reconciliation" description="Desired-state plans and provider provisioning share the same verified enterprise resource graph.">
              <div className="grid grid-cols-4 gap-2">
                <Mini label="Recent" value={overview?.flow?.deployment_plans?.total_recent ?? 0} />
                <Mini label="Ready" value={overview?.flow?.deployment_plans?.ready ?? 0} />
                <Mini label="Blocked" value={overview?.flow?.deployment_plans?.blocked ?? 0} />
                <Mini label="Done" value={overview?.flow?.deployment_plans?.completed ?? 0} />
              </div>
            </Panel>
          </div>

          <div className={flowSection === "releases" ? "" : "hidden"}>
          <Panel icon={AppWindow} title="Application Releases" description="Compose once, then deploy, reconcile, activate, promote or roll back through immutable environment releases.">
            <div className="space-y-3">
              {filteredFlowReleases.length === 0 && (
                <p className="text-xs text-foreground/35">No application releases have been composed yet.</p>
              )}
              {filteredFlowReleases.map((release) => {
                const app = (overview?.applications ?? []).find((item) => item.entity_key === release.application_entity_key);
                const steps = (overview?.deployment_plan_steps ?? [])
                  .filter((step) => step.plan_id === release.plan_id)
                  .sort((a,b) => a.step_order-b.step_order);
                const envs = (overview?.environments ?? []).slice().sort((a,b) => a.promotion_order-b.promotion_order);
                const envIndex = envs.findIndex((env) => env.environment_key === release.environment_key);
                const nextEnv = envIndex >= 0 ? envs[envIndex+1] : undefined;
                const rollbackTargets = (overview?.application_releases ?? [])
                  .filter((candidate) =>
                    candidate.application_entity_key === release.application_entity_key
                    && candidate.environment_key === release.environment_key
                    && candidate.release_id !== release.release_id
                    && candidate.release_number < release.release_number
                    && (candidate.health_state === "HEALTHY" || ["ACTIVE","RETIRED"].includes(candidate.state))
                  )
                  .sort((a,b) => b.release_number-a.release_number)
                  .slice(0,3);

                return (
                  <div key={release.release_id} className="rounded-xl border border-foreground/8 bg-foreground/[0.02] p-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold">{app?.display_name ?? release.application_entity_key}</p>
                        <p className="mt-1 text-[10px] text-foreground/42">{release.environment_key} · v{release.version} · Release {release.release_number}</p>
                        <p className="mt-1 font-mono text-[9px] text-foreground/32">{release.release_key}</p>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        <Badge className={tone(release.state)}>{release.state}</Badge>
                        <Badge className={tone(release.health_state)}>{release.health_state}</Badge>
                        {release.is_current && <Badge className="bg-primary/[0.08] text-primary">CURRENT</Badge>}
                      </div>
                    </div>

                    {steps.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {steps.map((step) => {
                          const providerOperation = step.provider_operation_id
                            ? (overview?.provider_operations ?? []).find((op) => op.operation_id === step.provider_operation_id)
                            : undefined;
                          return (
                            <div key={step.step_id} className="rounded-lg bg-background/60 p-2.5">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <div>
                                  <p className="text-[10px] font-semibold">{step.step_order}. {step.step_key}</p>
                                  <p className="mt-0.5 text-[9px] text-foreground/35">{step.action_class} · {step.provider_key ?? "RUNYI"} · {step.action_key ?? step.blueprint_key ?? "internal"}</p>
                                </div>
                                <Badge className={tone(step.status)}>{step.status}</Badge>
                              </div>
                              <div className="mt-2 flex flex-wrap gap-2">
                                {step.status === "READY" && (
                                  <Button size="sm" variant="outline" onClick={() => void releaseAction(
                                    "enterprise_release_apply_step",
                                    { release_id: release.release_id, step_id: step.step_id },
                                    "release_step",
                                    "Release plan step prepared through the existing execution fabric.",
                                  )}>Apply step</Button>
                                )}
                                {providerOperation?.approval_required && providerOperation.status === "PREPARED" && (
                                  <Button size="sm" variant="outline" onClick={() => void releaseAction(
                                    "enterprise_release_approve_provider_operation",
                                    { operation_id: providerOperation.operation_id },
                                    "release_provider_approval",
                                    "Exact provider operation approved.",
                                  )}>Approve exact provider operation</Button>
                                )}
                                {providerOperation && ["APPROVED","EXECUTION_PENDING"].includes(providerOperation.status) && (
                                  <Button size="sm" onClick={() => void releaseAction(
                                    "enterprise_release_dispatch_provider_operation",
                                    { operation_id: providerOperation.operation_id, approval_id: providerOperation.approval_id ?? null },
                                    "release_provider_dispatch",
                                    "Provider operation dispatched server-to-server.",
                                  )}>Execute provider operation</Button>
                                )}
                                {providerOperation && (
                                  <Badge className={tone(providerOperation.status)}>PROVIDER {providerOperation.status}</Badge>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div className="mt-3 flex flex-wrap gap-2">
                      {["READY","DEPLOYING","VERIFYING"].includes(release.state) && (
                        <Button size="sm" variant="outline" onClick={() => void releaseAction(
                          "enterprise_release_reconcile",
                          { release_id: release.release_id },
                          "release_reconcile",
                          "Whole application stack reconciled.",
                        )}>Reconcile stack</Button>
                      )}
                      {release.state === "VERIFYING" && release.health_state === "HEALTHY" && (
                        <Button size="sm" onClick={() => void releaseAction(
                          "enterprise_release_activate",
                          { release_id: release.release_id },
                          "release_activate",
                          "Verified application release activated.",
                        )}>Activate release</Button>
                      )}
                      {release.state === "ACTIVE" && release.is_current && nextEnv && (
                        <Button size="sm" variant="outline" onClick={() => void releaseAction(
                          "enterprise_release_promote",
                          {
                            source_release_id: release.release_id,
                            target_environment_key: nextEnv.environment_key,
                            target_tenant_project_ref: release.tenant_project_ref ?? null,
                          },
                          "release_promote",
                          "Next-environment release candidate prepared.",
                        )}>Promote to {nextEnv.environment_key}</Button>
                      )}
                      {release.state === "ACTIVE" && release.is_current && rollbackTargets.map((target) => (
                        <Button key={target.release_id} size="sm" variant="outline" onClick={() => void releaseAction(
                          "enterprise_release_rollback",
                          { current_release_id: release.release_id, target_release_id: target.release_id },
                          "release_rollback",
                          "Governed rollback release candidate prepared.",
                        )}>Rollback toward R{target.release_number}</Button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </Panel>
          </div>

          <div className={flowSection === "authorization" ? "" : "hidden"}>
          <Panel icon={ShieldCheck} title="Supabase Deployment Authorization" description="RUNYI never bypasses Supabase OAuth consent. Check whether a member project is ready for Auth, Database/pgvector and Edge Function deployment.">
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input value={scopeProjectRef} onChange={(e) => setScopeProjectRef(e.target.value)} placeholder="Member Supabase project ref" className="h-10 font-mono" />
              <Button size="sm" className="h-10" variant="outline" onClick={() => void checkSupabaseScopes()} disabled={!scopeProjectRef.trim() || busy === "scope_readiness"}>
                {busy === "scope_readiness" ? <LoaderCircle className="size-4 animate-spin" /> : <ShieldCheck className="size-4" />}
                Check scopes
              </Button>
            </div>
            {scopeReadiness && (
              <pre className="mt-3 max-h-64 overflow-auto rounded-xl bg-foreground/[0.025] p-3 text-[10px] leading-5 text-foreground/55">
                {JSON.stringify(scopeReadiness, null, 2)}
              </pre>
            )}
          </Panel>
          </div>

          <div className={flowSection === "provisioning" ? "space-y-3" : "hidden"}>
          <Panel icon={ServerCog} title="Create selected infrastructure" description="Prepare an INSTALLABLE provider-capability assignment. Cost and approval gates remain explicit; this button does not silently execute the provider.">
            <div className="grid gap-2 lg:grid-cols-[minmax(0,1fr)_180px]">
              <Select value={flowAssignmentId} onValueChange={initializeFlowAssignment}>
                <SelectTrigger className="h-10"><SelectValue placeholder="Choose DESIRED infrastructure assignment" /></SelectTrigger>
                <SelectContent>
                  {(overview?.assignments ?? [])
                    .filter((item) => item.state === "DESIRED" && item.target_entity_key.startsWith("PROVIDER_CAPABILITY:"))
                    .map((item) => {
                      const app = (overview?.applications ?? []).find((appItem) => appItem.entity_key === item.application_entity_key);
                      return (
                        <SelectItem key={item.assignment_id} value={item.assignment_id}>
                          {(app?.display_name ?? item.application_entity_key) + " · " + item.assignment_slot.replaceAll("_", " ") + " · " + item.target_entity_key.replace("PROVIDER_CAPABILITY:", "")}
                        </SelectItem>
                      );
                    })}
                </SelectContent>
              </Select>
              <Input value={flowCostRef} onChange={(e) => setFlowCostRef(e.target.value)} placeholder="Cost confirmation ref (optional)" className="h-10" />
            </div>

            {flowAssignmentId && (
              <div className="mt-3 grid gap-3 lg:grid-cols-2">
                <div>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-foreground/38">Provider input</p>
                  <Textarea value={flowProviderPayload} onChange={(e) => setFlowProviderPayload(e.target.value)} className="min-h-40 font-mono text-[11px]" />
                </div>
                <div>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-foreground/38">RUNYI resource registration</p>
                  <Textarea value={flowResourceSpec} onChange={(e) => setFlowResourceSpec(e.target.value)} className="min-h-40 font-mono text-[11px]" />
                </div>
              </div>
            )}

            <Button size="sm" className="mt-3 h-10" onClick={() => void prepareFlowAssignment()} disabled={!flowAssignmentId || busy === "flow_prepare"}>
              {busy === "flow_prepare" ? <LoaderCircle className="size-4 animate-spin" /> : <Workflow className="size-4" />}
              Prepare governed provisioning
            </Button>
          </Panel>

          <div className="space-y-3">
            {filteredProvisioningRequests.map((request) => {
              const steps = (overview?.provisioning_steps ?? []).filter((step) => step.request_id === request.request_id).sort((a,b) => a.step_order-b.step_order);
              const assignment = (overview?.assignments ?? []).find((item) => String(item.metadata?.provisioning_request_id ?? "") === request.request_id);
              return (
                <Card key={request.request_id} className="border-foreground/8 bg-card py-0">
                  <CardContent className="p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold">{request.blueprint_key}</p>
                        <p className="mt-1 font-mono text-[9px] text-foreground/35">{request.request_id}</p>
                        <p className="mt-1 text-[10px] text-foreground/45">{request.provider_key ?? "Provider unresolved"} · {request.environment_key}</p>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        <Badge className={tone(request.status)}>{request.status}</Badge>
                        <Badge className={tone(request.cost_state)}>COST {request.cost_state}</Badge>
                        <Badge className={tone(request.approval_state)}>APPROVAL {request.approval_state}</Badge>
                      </div>
                    </div>

                    <div className="mt-3 space-y-2">
                      {steps.map((step) => (
                        <div key={step.step_id} className="rounded-xl bg-foreground/[0.025] p-3">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div>
                              <p className="text-[10px] font-semibold">{step.step_order}. {step.step_key}</p>
                              <p className="mt-0.5 text-[9px] text-foreground/38">{step.action_key ?? step.step_type}</p>
                            </div>
                            <Badge className={tone(step.status)}>{step.status}</Badge>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {step.provider_operation_id && step.status === "BLOCKED" && (
                              <Button size="sm" variant="outline" onClick={() => void flowInvoke("enterprise_flow_approve_provider_step", { step_id: step.step_id }, "provider_approval")}>
                                Approve provider operation
                              </Button>
                            )}
                            {step.provider_operation_id && step.status === "READY" && (
                              <Button size="sm" onClick={() => void flowInvoke("enterprise_flow_dispatch_step", { step_id: step.step_id }, "provider_dispatch")}>
                                Execute provider step
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      {request.cost_state === "REQUIRED" && (
                        <Button size="sm" variant="outline" onClick={() => {
                          const ref = window.prompt("Cost confirmation reference");
                          if (ref) void flowInvoke("enterprise_flow_confirm_cost", { request_id: request.request_id, cost_confirmation_ref: ref }, "cost_confirmation");
                        }}>Confirm cost</Button>
                      )}
                      {request.approval_state === "REQUIRED" && request.cost_state !== "REQUIRED" && (
                        <Button size="sm" variant="outline" onClick={() => void flowInvoke("enterprise_flow_approve_request", { request_id: request.request_id }, "request_approval")}>
                          Approve provisioning
                        </Button>
                      )}
                      {request.status === "READY" && steps.every((step) => !step.provider_operation_id) && (
                        <Button size="sm" onClick={() => void flowInvoke("enterprise_flow_prepare_step", { request_id: request.request_id }, "step_prepare")}>
                          Prepare next provider step
                        </Button>
                      )}
                      {["EXECUTING","VERIFYING"].includes(request.status) && (
                        <Button size="sm" variant="outline" onClick={() => void flowInvoke("enterprise_flow_reconcile_request", { request_id: request.request_id }, "reconcile")}>
                          Reconcile
                        </Button>
                      )}
                      {request.status === "VERIFYING" && steps.every((step) => ["COMPLETED","SKIPPED"].includes(step.status)) && (
                        <Button size="sm" onClick={() => void flowInvoke("enterprise_flow_finalize_request", { request_id: request.request_id }, "finalize")}>
                          Verify & register resource
                        </Button>
                      )}
                      {request.status === "COMPLETED" && assignment?.state === "DESIRED" && (
                        <Button size="sm" onClick={() => void flowInvoke("enterprise_flow_activate_assignment", { assignment_id: assignment.assignment_id }, "activate")}>
                          Activate app assignment
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          </div>

          <div className={flowSection === "overview" ? "grid gap-3 lg:grid-cols-2" : "hidden"}>
            <Panel icon={Network} title="Enterprise orchestration model" description="Bridge selects. Flow provisions. Verification registers. Only verified concrete resources become ACTIVE app assignments.">
              <div className="flex flex-wrap gap-1.5 text-[10px]">
                {["DESIRED","COST","APPROVE","EXECUTE","VERIFY","REGISTER","ACTIVATE"].map((item) => (
                  <Badge key={item} className="bg-background/70 text-foreground/55">{item}</Badge>
                ))}
              </div>
            </Panel>
            <Panel icon={CircleAlert} title="Authority boundary" description="Flow does not bypass Owner Root, M4, cost or exact provider approval. Unsupported catalogue capabilities remain non-installable.">
              <p className="text-xs leading-5 text-foreground/48">The browser never receives RUNYI service-role or provider credentials. Provider dispatch occurs server-to-server.</p>
            </Panel>
          </div>
        </div>
      )}

      {mode === "infrastructure" && infrastructureDetail && (
        <InfrastructureLiveScreen
          selection={infrastructureDetail}
          overview={overview}
          supabase={supabase}
          connected={connected}
          loading={loading}
          onBack={() => setInfrastructureDetail(null)}
          onRefresh={() => void refresh()}
          onChanged={() => void refresh()}
        />
      )}

      {mode === "applications" && configApplication && (
        <AppsConfigurationEngine app={configApplication}
          assignments={(overview?.assignments ?? []).filter((a) => a.application_entity_key === configApplication.entity_key)}
          releases={(overview?.application_releases ?? []).filter((r) => r.application_entity_key === configApplication.entity_key)}
          resources={overview?.resources ?? []} backendServices={overview?.backend_services ?? []}
          supabase={supabase} connected={connected} onClose={() => setAppsConfigKey(null)}
          onRefresh={() => void refresh()}
          onOpenTest={() => setAppsTestTarget({ appKey: configApplication.entity_key, projectName: null })} />
      )}
      {mode === "applications" && appsTestTarget && (
        <AppsOpenTestScreen app={testedApplication}
          releases={(overview?.application_releases ?? []).filter((r) => r.application_entity_key === appsTestTarget.appKey)}
          projectName={appsTestTarget.projectName ?? undefined} onBack={() => setAppsTestTarget(null)} />
      )}

      {notice && (
        <p className="rounded-xl bg-primary/[0.06] px-3 py-2 text-xs leading-5 text-foreground/65">{notice}</p>
      )}
    </div>
  );
}

function InfrastructureLiveScreen({
  selection,
  overview,
  supabase,
  connected,
  loading,
  onBack,
  onRefresh,
  onChanged,
}: {
  selection: InfrastructureDetailSelection;
  overview: Overview | null;
  supabase: SupabaseClient | null;
  connected: boolean;
  loading: boolean;
  onBack: () => void;
  onRefresh: () => void;
  onChanged: () => void;
}) {
  const resource = selection.kind === "RESOURCE"
    ? (overview?.resources ?? []).find((item) => item.resource_key === selection.key) ?? null
    : null;
  const capability = selection.kind === "CAPABILITY"
    ? (overview?.marketplace ?? []).find((item) => item.provider_capability_key === selection.key) ?? null
    : null;
  const backendService = selection.kind === "BACKEND_SERVICE"
    ? (overview?.backend_services ?? []).find((item) => item.backend_service_key === selection.key) ?? null
    : null;

  if (!resource && !capability && !backendService) return null;

  const targetEntityKey = resource
    ? `RESOURCE:${resource.resource_key}`
    : capability
      ? `PROVIDER_CAPABILITY:${capability.provider_capability_key}`
      : `BACKEND_SERVICE:${backendService!.backend_service_key}`;
  const assignments = (overview?.assignments ?? []).filter((item) => item.target_entity_key === targetEntityKey);
  const title = resource?.display_name ?? capability?.display_name ?? backendService!.display_name;
  const providerKey = resource?.provider_key ?? capability?.provider_key ?? backendService?.provider_key ?? "RUNYI";
  const categoryKey = resource ? categoryForResourceType(resource.resource_type) : capability?.category_key ?? "BACKEND_SERVICE";
  const displayIdentity = resource
    ? resolveResourceDisplayIdentity(overview, resource)
    : capability
      ? resolveCapabilityDisplayIdentity(overview, capability)
      : resolveBackendServiceDisplayIdentity(overview, backendService!);
  const identityEmail = displayIdentity.email;
  const projectRef = resource?.tenant_project_ref ?? backendService?.provider_project_ref ?? null;
  const environment = resource?.environment_key ?? backendService?.environment_key ?? "PROVIDER CATALOGUE";
  const lifecycleState = resource?.lifecycle_state ?? backendService?.lifecycle_state ?? capability?.state ?? "CATALOGUED";
  const healthState = resource?.health_state ?? backendService?.health_state ?? capability?.runyi_coverage_state ?? "UNKNOWN";
  const syncState = resource?.sync_state ?? backendService?.sync_state ?? (capability ? "CATALOGUED" : "UNKNOWN");
  const applications = assignments.map((assignment) => {
    const app = (overview?.applications ?? []).find((item) => item.entity_key === assignment.application_entity_key);
    return { assignment, name: app?.display_name ?? assignment.application_entity_key };
  });

  return (
    <div className="fixed inset-0 z-[90] flex flex-col bg-background text-foreground">
      <header className="shrink-0 border-b border-foreground/8 bg-[var(--top-bar)]/98 px-3 pb-2.5 backdrop-blur-xl" style={{ paddingTop: "max(10px, env(safe-area-inset-top))" }}>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onBack} className="grid size-9 shrink-0 place-items-center rounded-xl border border-foreground/10 bg-foreground/[0.025]" aria-label="Back to Marketplace">
            <ArrowLeft className="size-4" />
          </button>
          <div className="min-w-0 flex-1">
            <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-primary">RUNYI live infrastructure</p>
            <h2 className="truncate text-[16px] font-semibold">{title}</h2>
          </div>
          <button type="button" onClick={onRefresh} disabled={loading} className="grid size-9 shrink-0 place-items-center rounded-xl border border-foreground/10 bg-foreground/[0.025]" aria-label="Refresh current system truth">
            {loading ? <LoaderCircle className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
          </button>
          {resource && !resource.immutable && resource.authority_class !== "ENTERPRISE_ROOT" ? (
            <UniversalInfrastructureActions
              supabase={supabase}
              connected={connected}
              applications={overview?.applications ?? []}
              target={{
                target_entity_key: targetEntityKey,
                display_name: title,
                category_key: categoryKey,
                provider_key: providerKey,
                target_kind: "INSTALLED",
                environment_key: environment,
                provider_account_identity: identityEmail,
                identity_email: resource.identity_email,
                identity_email_type: resource.identity_email_type,
                identity_email_verified: resource.identity_email_verified,
                identity_email_source: resource.identity_email_source,
                sync_state: resource.sync_state,
                last_synced_at: resource.last_synced_at ?? null,
                provider_project_ref: projectRef,
                region_ref: resource.region_ref ?? null,
                lifecycle_state: lifecycleState,
                health_state: healthState,
                capabilities: resource.capabilities ?? [],
              }}
              assignments={assignments}
              onChanged={onChanged}
            />
          ) : null}
        </div>
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3" style={{ paddingBottom: "max(28px, env(safe-area-inset-bottom))" }}>
        <div className="mx-auto max-w-2xl space-y-3">
          <section className="rounded-2xl border border-foreground/8 bg-card px-3 py-3">
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge className={tone(lifecycleState)}>{lifecycleState}</Badge>
              <Badge className={tone(healthState)}>{healthState}</Badge>
              <Badge className={tone(syncState)}>{syncState}</Badge>
              <Badge className="bg-foreground/[0.05] text-foreground/60">{providerKey}</Badge>
            </div>
            <div className="mt-3 divide-y divide-foreground/7">
              <LiveField label="Human identity" value={identityEmail} />
              <LiveField label="Identity display source" value={displayIdentitySource(displayIdentity.source)} />
              <LiveField label="Provider project" value={projectRef || "Not applicable / unresolved"} mono />
              <LiveField label="Environment" value={environment} />
              <LiveField label="RUNYI identity" value={targetEntityKey} mono />
              {resource?.region_ref ? <LiveField label="Region" value={resource.region_ref} /> : null}
              {resource?.last_synced_at ? <LiveField label="Last synchronized" value={new Date(resource.last_synced_at).toLocaleString()} /> : null}
              {backendService?.last_synced_at ? <LiveField label="Last synchronized" value={new Date(backendService.last_synced_at).toLocaleString()} /> : null}
            </div>
          </section>

          {capability ? (
            <section className="rounded-2xl border border-foreground/8 bg-card px-3 py-3">
              <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-foreground/48">Capability state</p>
              <div className="mt-2 grid grid-cols-2 gap-1.5">
                <Mini label="Installed instances" value={capability.installed_resource_count} />
                <Mini label="Assigned apps" value={capability.assigned_application_count} />
              </div>
              <p className="mt-2 text-[10px] leading-5 text-foreground/48">Generic catalogue definitions stay provider-neutral. Open a connected project or installed resource to see its verified provider-account email and project identity.</p>
            </section>
          ) : null}

          {resource && Array.isArray(resource.metadata?.component_resources) && resource.metadata.component_resources.length > 0 ? (
            <section className="rounded-2xl border border-foreground/8 bg-card px-3 py-3">
              <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-foreground/48">Project resources</p>
              <div className="mt-2 divide-y divide-foreground/7">
                {resource.metadata.component_resources.map((key) => (
                  <div key={String(key)} className="py-2 font-mono text-[9px] text-foreground/58">{String(key)}</div>
                ))}
              </div>
            </section>
          ) : null}

          {backendService ? (
            <section className="rounded-2xl border border-foreground/8 bg-card px-3 py-3">
              <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-foreground/48">Backend service runtime</p>
              <div className="mt-2 divide-y divide-foreground/7">
                <LiveField label="Role" value={backendService.logical_role.replaceAll("_", " ")} />
                <LiveField label="Runtime" value={backendService.runtime_target_entity_key || "Runtime pending"} mono />
                <LiveField label="Verification" value={backendService.verification_state} />
              </div>
            </section>
          ) : null}

          <section className="rounded-2xl border border-foreground/8 bg-card px-3 py-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-foreground/48">Applications using this</p>
              <span className="text-[9px] text-foreground/40">{applications.length}</span>
            </div>
            <div className="mt-2 divide-y divide-foreground/7">
              {applications.length === 0 ? (
                <p className="py-3 text-[10px] text-foreground/42">No application assignments.</p>
              ) : applications.map(({ assignment, name }) => (
                <div key={assignment.assignment_id} className="flex items-center justify-between gap-3 py-2">
                  <div className="min-w-0"><p className="truncate text-[11px] font-semibold">{name}</p><p className="mt-0.5 text-[8px] text-foreground/40">{assignment.environment_key} · {assignment.assignment_role}</p></div>
                  <Badge className={tone(assignment.state)}>{assignment.state}</Badge>
                </div>
              ))}
            </div>
          </section>

          <p className="px-1 text-[9px] leading-4 text-foreground/35">This screen projects synchronized RUNYI system truth. Provider events update it first; focus/foreground and scheduled reconciliation are fallback freshness checks.</p>
        </div>
      </main>
    </div>
  );
}

function LiveField({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <span className="shrink-0 text-[9px] text-foreground/40">{label}</span>
      <span className={`min-w-0 break-all text-right text-[10px] text-foreground/68 ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

function WorkspaceTabs({
  items,
  value,
  onChange,
}: {
  items: Array<{ id: string; label: string; count?: number }>;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="scrollbar-none flex gap-1 overflow-x-auto rounded-2xl border border-foreground/8 bg-card p-1">
      {items.map((item) => (
        <button
          type="button"
          key={item.id}
          onClick={() => onChange(item.id)}
          className={`flex shrink-0 items-center gap-1.5 rounded-xl px-3 py-2 text-[10px] font-semibold transition ${
            value === item.id ? "bg-foreground/[0.09] text-foreground shadow-sm" : "text-foreground/50 hover:bg-foreground/[0.04] hover:text-foreground/80"
          }`}
        >
          {item.label}
          {item.count !== undefined && <span className="rounded-md bg-foreground/[0.07] px-1.5 py-0.5 text-[9px] text-foreground/55">{item.count}</span>}
        </button>
      ))}
    </div>
  );
}

function WorkspaceTile({
  icon: Icon,
  title,
  meta,
  onClick,
}: {
  icon: LucideIcon;
  title: string;
  meta: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-[92px] items-start gap-3 rounded-2xl border border-foreground/8 bg-card p-3 text-left shadow-sm transition hover:-translate-y-px hover:border-foreground/15 hover:bg-foreground/[0.02]"
    >
      <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-primary/[0.08] text-primary">
        <Icon className="size-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-xs font-semibold">{title}</span>
        <span className="mt-1 block text-[10px] leading-4 text-foreground/48">{meta}</span>
      </span>
      <ChevronRight className="mt-1 size-3.5 shrink-0 text-foreground/30" />
    </button>
  );
}

function Filters({
  search,setSearch,category,setCategory,provider,setProvider,categories,providers,hideCategory=false,
}: {
  search: string;
  setSearch: (value: string) => void;
  category: string;
  setCategory: (value: string) => void;
  provider: string;
  setProvider: (value: string) => void;
  categories: CatalogueCategory[];
  providers: string[];
  hideCategory?: boolean;
}) {
  return (
    <div className={"grid gap-1.5 " + (hideCategory ? "sm:grid-cols-[minmax(0,1fr)_180px]" : "sm:grid-cols-[minmax(0,1fr)_220px_180px]")}>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-foreground/30" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search infrastructure, providers or capabilities" className="h-9 rounded-lg pl-8 text-sm" />
      </div>
      {!hideCategory && (
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All categories</SelectItem>
            {categories.map((item) => <SelectItem key={item.category_key} value={item.category_key}>{item.display_name}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      <Select value={provider} onValueChange={setProvider}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="ALL">All providers</SelectItem>
          {providers.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function Panel({
  icon: Icon,title,description,children,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <Card className="border-foreground/8 bg-card py-0">
      <CardContent className="p-3.5">
        <div className="mb-2.5 flex items-start gap-2.5">
          <div className="grid size-8 shrink-0 place-items-center rounded-xl bg-primary/[0.08] text-primary">
            <Icon className="size-4" />
          </div>
          <div>
            <p className="text-sm font-semibold">{title}</p>
            <p className="mt-1 text-[11px] leading-5 text-foreground/58">{description}</p>
          </div>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

function Metric({ label,value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div className="rounded-xl bg-foreground/[0.025] px-2.5 py-2">
      <p className="text-[8px] uppercase tracking-wider text-foreground/45">{label}</p>
      {value == null
        ? <span className="mt-1 block h-4 w-10 animate-pulse rounded bg-foreground/10" aria-label={`${label} loading`} />
        : <p className="mt-0.5 text-sm font-semibold">{value}</p>}
    </div>
  );
}

function Mini({ label,value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg bg-foreground/[0.025] px-2.5 py-1.5">
      <p className="text-[8px] text-foreground/45">{label}</p>
      <p className="mt-0.5 text-[11px] font-semibold">{value}</p>
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="grid min-h-[320px] place-items-center rounded-2xl border border-foreground/8 bg-card p-8 text-center text-sm text-foreground/45">
      <div>
        <HardDrive className="mx-auto mb-3 size-7 text-primary" />
        <p className="max-w-md leading-6">{text}</p>
      </div>
    </div>
  );
}
